import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, Timestamp, setDoc, writeBatch,
  deleteField, limit,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
  findShiftClash, waNumber, whatsappUrl, weekScheduleText, personScheduleText, weekRangeLabel, weekRelLabel,
  clearWeekTargets, orderStaff, upcomingShifts,
} from './plannerUtils';
import { loadTeamSettings, saveTeamOrder, markInactive, markActive } from './teamSettings';
import TeamManager from './TeamManager';
import {
  toDate, isDeleted, findTimesheetIssues, overlappingRecords, workedMinutes, duplicatePendingRequests,
} from './timesheetUtils';
import { submitShiftRequest } from './shiftRequestsCloud';
import { findAnomalies, topSeverity, ANOMALY_GROUPS } from './anomalyUtils';
import './Shifts.css';

const fmtTime = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

const MANAGER_ROLES = ['owner', 'admin', 'manager'];
const ADMIN_ROLES = ['owner', 'admin', 'manager']; // can see hours & reports
const STAFF_ROLES = ['employee', 'manager'];
const ALL_STAFF_ROLES = ['employee', 'manager', 'admin', 'owner'];

/** Only employees/managers in the dropdown (filter out admin/owner/customer). */
function staffOnly(employees, staff) {
  const staffIds = new Set(staff.map(s => s.uid));
  return employees.filter(e => staffIds.size === 0 || staffIds.has(e.id));
}

// ─── Planner helpers ─────────────────────────────────────────────────────────
const POSITIONS = [
  { label: 'Cook',     color: '#f59e0b', bg: 'rgba(245,158,11,0.18)' },
  { label: 'Cashier',  color: '#22c55e', bg: 'rgba(34,197,94,0.16)' },
  { label: 'Server',   color: '#3b82f6', bg: 'rgba(59,130,246,0.16)' },
  { label: 'Prep',     color: '#a78bfa', bg: 'rgba(167,139,250,0.16)' },
  { label: 'Delivery', color: '#f472b6', bg: 'rgba(244,114,182,0.16)' },
  { label: 'Manager',  color: '#06b6d4', bg: 'rgba(6,182,212,0.16)' },
  { label: 'Cleaning', color: '#94a3b8', bg: 'rgba(148,163,184,0.16)' },
  { label: 'Other',    color: '#e2e8f0', bg: 'rgba(226,232,240,0.12)' },
];
const SHIFT_TEMPLATES = [
  { label: 'Opening',    startTime: '11:00', endTime: '14:00' },
  { label: 'Morning',   startTime: '09:00', endTime: '15:00' },
  { label: 'Mid',       startTime: '10:00', endTime: '15:00' },
  { label: 'Closing',   startTime: '16:30', endTime: '22:00' },
  { label: 'Evening',   startTime: '18:00', endTime: '22:00' },
  { label: 'Evening Mid', startTime: '19:00', endTime: '23:00' },
];
// Mon–Sun week: day 1=Mon … 0=Sun
const PLAN_DAYS      = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const PLAN_DAYS_FULL = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
function pToISO(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
function pAddDays(iso, n) { const d = new Date(iso+'T00:00:00'); d.setDate(d.getDate()+n); return pToISO(d); }
function pGetWeekMonday(d) {
  const dt = new Date(d); const day = dt.getDay();
  dt.setDate(dt.getDate() - (day === 0 ? 6 : day - 1)); return pToISO(dt);
}
function pFmt12(hhmm) {
  if (!hhmm) return ''; const [h, m] = hhmm.split(':').map(Number);
  return `${h%12||12}:${String(m).padStart(2,'0')}${h>=12?'pm':'am'}`;
}
function pShiftHours(s, e) {
  if (!s||!e) return 0;
  const [sh,sm]=s.split(':').map(Number), [eh,em]=e.split(':').map(Number);
  return Math.max(0, (eh*60+em - sh*60-sm)/60);
}
function pPosMeta(label) { return POSITIONS.find(p=>p.label===label)||POSITIONS[POSITIONS.length-1]; }
function pInitials(name) { if(!name) return '?'; return name.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2); }
function pFmtDate(iso) { const d=new Date(iso+'T00:00:00'); return d.toLocaleDateString('en-US',{month:'short',day:'numeric'}); }
function pIsToday(iso) { return iso===pToISO(new Date()); }
function pDayName(iso) {
  const d = new Date(iso + 'T00:00:00');
  return PLAN_DAYS_FULL[d.getDay() === 0 ? 6 : d.getDay() - 1];
}
function getAvailStatus(availMap, userId, dateISO, startTime, endTime) {
  const avail = availMap?.[userId];
  if (!avail?.days) return 'unknown';
  const dayName = pDayName(dateISO);
  const day = avail.days[dayName];
  if (!day) return 'unknown';
  if (day.type === 'unavailable') return 'unavailable';
  if (day.type === 'available') return 'available';
  if (day.type === 'partial') {
    if (!startTime || !endTime || !day.startTime || !day.endTime) return 'partial';
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const [ah, am] = day.startTime.split(':').map(Number);
    const [bh, bm] = day.endTime.split(':').map(Number);
    const sMin = sh*60+sm, eMin = eh*60+em, aMin = ah*60+am, bMin = bh*60+bm;
    if (sMin >= aMin && eMin <= bMin) return 'available';
    return 'partial';
  }
  return 'unknown';
}

// A person's live clock records touching [start, end] — clock-ins up to 16h earlier can still run into it
async function fetchRecordOverlaps(userId, start, end) {
  const snap = await getDocs(query(collection(db, 'shifts'),
    where('clockIn', '>=', Timestamp.fromDate(new Date(+start - 16 * 3600e3))),
    where('clockIn', '<=', Timestamp.fromDate(end))));
  const list = [];
  snap.forEach(d => list.push({ id: d.id, ...d.data() }));
  return overlappingRecords(list, { userId, start, end });
}

const ShiftDashboard = () => {
  const { user, role, loading } = useAuth();
  const navigate = useNavigate();
  const canViewHours  = ADMIN_ROLES.includes(role);
  const canEdit        = MANAGER_ROLES.includes(role);       // manager+ can propose edits
  const canDirectEdit  = ['owner', 'admin'].includes(role); // admin/owner save directly
  const canApprove     = ['owner', 'admin'].includes(role); // admin/owner approve requests
  // Managers delete clock records directly too (soft delete — restorable, with who & why);
  // they must give a reason, owners/admins may skip it
  const canDeleteRecords = MANAGER_ROLES.includes(role);
  const deleteNeedsReason = !canDirectEdit;

  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const allStaffRef = useRef([]);           // latest allStaff, for loadShifts
  // 👥 Team: saved display order + who's inactive for scheduling (shiftSettings/team)
  const [team, setTeam] = useState({ order: [], inactive: {} });
  const [teamOpen, setTeamOpen] = useState(false);
  const [teamSave, setTeamSave] = useState('idle'); // idle | saving | saved | error
  const teamSaveTimer = useRef(null);
  const pendingOrderRef = useRef(null);
  const savedOrderRef = useRef([]);               // last order the database has — restored if a save fails
  const shiftEmployeesRef = useRef([]);     // everyone who has a shift in the loaded range
  const [activeShiftsMap, setActiveShiftsMap] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  const [busy, setBusy] = useState(null);
  const [dateRange, setDateRange] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [todaySort, setTodaySort] = useState('team'); // Today's Scheduled Shifts: 'team' order or by 'time'
  // Anomalies (Hours & Reports): clock records vs the assigned shifts of the period
  const [rangePlanned, setRangePlanned] = useState([]);
  const [anomalyFilter, setAnomalyFilter] = useState('all');
  const [showReviewed, setShowReviewed] = useState(false);
  const [ackBusy, setAckBusy] = useState(null);
  // Shift Log: review / clean up timesheets
  const [showDeleted, setShowDeleted] = useState(false); // show soft-deleted records (restore)
  const [onlyIssues, setOnlyIssues] = useState(false);   // only possible duplicates / mis-taps
  const [editDelete, setEditDelete] = useState(null);    // { reason } while confirming a delete in the edit modal
  const [addOverlaps, setAddOverlaps] = useState([]);    // manual add: records it would sit on top of
  const [replaceOverlaps, setReplaceOverlaps] = useState(true);
  const [reqOverlaps, setReqOverlaps] = useState({});    // pending add-shift request id → overlapping records
  const editSavingRef = useRef(false);
  const [now, setNow] = useState(new Date());

  // Edit shift state
  const [editModal, setEditModal] = useState(null);
  const [editClockIn, setEditClockIn] = useState('');
  const [editClockOut, setEditClockOut] = useState('');
  const [editEmployee, setEditEmployee] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Hour-change request approval state
  const [pendingRequests, setPendingRequests] = useState([]);
  const [reqBusy, setReqBusy] = useState(null); // request id being processed
  const [managerEditReason, setManagerEditReason] = useState('');
  const [reqHistory, setReqHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showReqHistory, setShowReqHistory] = useState(false);

  // ── Planner state ──
  const [dashView, setDashView] = useState('dashboard'); // 'dashboard' | 'planner'
  const [weekStart, setWeekStart] = useState(() => pGetWeekMonday(new Date()));
  const [planned, setPlanned] = useState([]);
  const [planLoading, setPlanLoading] = useState(true);
  const [actualMap, setActualMap] = useState({});
  // While the planner shows another week, the Dashboard's "today" comes from here: { planned, actual }
  const [todayData, setTodayData] = useState(null);
  const [planFilterRole, setPlanFilterRole] = useState('employee');
  const [copyBusy, setCopyBusy] = useState(false);
  const [showPlanPreview, setShowPlanPreview] = useState(false);
  // WhatsApp group message layout: 'person' (name → days) or 'day' (day → time → names); per device
  const [shareLayout, setShareLayout] = useState(() => {
    try { return localStorage.getItem('sp_share_layout') === 'day' ? 'day' : 'person'; } catch { return 'person'; }
  });
  const [planToast, setPlanToast] = useState(null); // { msg, action?: { label, run } }
  const toastTimerRef = useRef(null);
  // latest planned week, read by Undo after the render that deleted the shift
  const plannedRef = useRef([]);
  const weekStartRef = useRef('');
  // planner modal
  const [planModal, setPlanModal] = useState(null); // {mode,userId,userName,date,doc?}
  const [pmStart,    setPmStart]    = useState('09:00');
  const [pmEnd,      setPmEnd]      = useState('17:00');
  const [pmPosition, setPmPosition] = useState('Cook');
  const [pmNotes,    setPmNotes]    = useState('');
  const [pmSaving,   setPmSaving]   = useState(false);
  const [pmAvailOverride, setPmAvailOverride] = useState(false);
  const [pmRepeat,   setPmRepeat]   = useState([]);    // other days (ISO) to add the same shift on
  const [pmConfirmDel, setPmConfirmDel] = useState(false);
  // "Clear week" confirm: { scope: 'view'|'all', keepPast } while open
  const [clearModal, setClearModal] = useState(null);
  const [clearBusy,  setClearBusy]  = useState(false);

  // ── Availability state ──
  const [availMap, setAvailMap] = useState({});
  const [availModal, setAvailModal] = useState(null); // {userId, userName}
  const [availDays, setAvailDays] = useState({});     // editing state for modal
  const [availSaving, setAvailSaving] = useState(false);
  // Availability tab UI state
  const [availSearchQ, setAvailSearchQ] = useState('');
  const [availFilterRole, setAvailFilterRole] = useState('all');
  const [availViewMode, setAvailViewMode] = useState('cards'); // 'grid' | 'cards'
  const [availInlineEdit, setAvailInlineEdit] = useState(null); // {userId, dayName}
  const [availCellSaving, setAvailCellSaving] = useState(null); // 'userId|dayName'
  const [availBulkSaving, setAvailBulkSaving] = useState(false);
  const [planSortMode, setPlanSortMode] = useState('team'); // 'team' | 'name' | 'hours' | 'avail'

  // ── Labor cost state ──
  const [laborRates, setLaborRates] = useState({}); // userId → hourly rate
  const [rateModal, setRateModal] = useState(null); // {userId, userName}
  const [rateInput, setRateInput] = useState('');
  const [rateSaving, setRateSaving] = useState(false);

  // ── Shift swap state ──
  const [swapRequests, setSwapRequests] = useState([]);
  const [swapModal, setSwapModal] = useState(null); // {shiftId, shiftDate, ...}
  const [swapTargetUser, setSwapTargetUser] = useState('');
  const [swapSaving, setSwapSaving] = useState(false);

  // shiftId → pending request — for managers to track their own submitted-but-pending edits
  const myPendingEditsMap = useMemo(() => {
    if (!user || role !== 'manager') return {};
    const map = {};
    pendingRequests.forEach((r) => {
      if (r.submittedBy !== user.uid) return;
      const key = r.type === 'new-shift' ? `new-${r.id}` : r.shiftId;
      if (key) map[key] = r;
    });
    return map;
  }, [pendingRequests, role, user]);

  const weekDays = useMemo(() => Array.from({length:7},(_,i)=>pAddDays(weekStart,i)), [weekStart]);
  const planMap = useMemo(() => {
    const m={}; planned.forEach(p=>{const k=`${p.userId}|${p.date}`;if(!m[k])m[k]=[];m[k].push(p);}); return m;
  }, [planned]);
  const empPlanHours = useMemo(() => {
    const h={}; planned.forEach(p=>{if(p.status==='cancelled')return;h[p.userId]=(h[p.userId]||0)+pShiftHours(p.startTime,p.endTime);}); return h;
  }, [planned]);
  const dayPlanHours = useMemo(() => {
    const h={}; planned.forEach(p=>{if(p.status==='cancelled')return;h[p.date]=(h[p.date]||0)+pShiftHours(p.startTime,p.endTime);}); return h;
  }, [planned]);
  const todayISO = pToISO(new Date());
  const thisWeekStart = pGetWeekMonday(new Date());
  const viewingThisWeek = weekStart === thisWeekStart;
  // Dashboard = today, whatever week the planner is showing
  const todayPlanSource = todayData ? todayData.planned : planned;
  const todayActualMap  = todayData ? todayData.actual  : actualMap;
  const todayScheduled = useMemo(() => todayPlanSource.filter(p=>p.date===todayISO&&p.status!=='cancelled')
    .sort((a,b)=>a.startTime.localeCompare(b.startTime)), [todayPlanSource, todayISO]);
  // Everyone in the team's saved order; inactive people drop out of planning, roster and lists
  const teamStaff = useMemo(() => orderStaff(allStaff, team.order), [allStaff, team.order]);
  const activeStaff = useMemo(() => teamStaff.filter(s => !team.inactive[s.uid]), [teamStaff, team.inactive]);
  const isInactive = uid => !!team.inactive[uid];
  // Each person's place in the 👥 Team order (unknown → last) — the Dashboard's lists sort by it too
  const teamRank = useMemo(() => new Map(teamStaff.map((s, i) => [s.uid, i])), [teamStaff]);
  const rankOf = uid => (teamRank.has(uid) ? teamRank.get(uid) : teamRank.size);
  // Today's list (and its late alerts): team order with each person's shifts by time, or plain time order
  const todayShown = useMemo(() => {
    if (todaySort === 'time') return todayScheduled;
    const r = uid => (teamRank.has(uid) ? teamRank.get(uid) : teamRank.size);
    return [...todayScheduled].sort((a, b) => r(a.userId) - r(b.userId) || a.startTime.localeCompare(b.startTime));
  }, [todayScheduled, todaySort, teamRank]);
  // Planner rows: active people, plus inactive ones who still have shifts this week (so no hours hide)
  const plannerStaff = useMemo(() => {
    const scheduled = new Set(planned.map(p => p.userId));
    return teamStaff.filter(s => !team.inactive[s.uid] || scheduled.has(s.uid));
  }, [teamStaff, team.inactive, planned]);
  const visiblePlanStaff = useMemo(() => {
    if(planFilterRole==='all') return plannerStaff.filter(s=>ALL_STAFF_ROLES.includes(s.role));
    if(planFilterRole==='manager') return plannerStaff.filter(s=>['manager','admin','owner'].includes(s.role));
    return plannerStaff.filter(s=>s.role==='employee');
  }, [plannerStaff, planFilterRole]);

  const pmAvailStatus = useMemo(() => {
    if (!planModal) return 'unknown';
    return getAvailStatus(availMap, planModal.userId, planModal.date, pmStart, pmEnd);
  }, [availMap, planModal, pmStart, pmEnd]);

  plannedRef.current = planned;
  weekStartRef.current = weekStart;

  // ── Planner modal: no double-booking ──
  const pmHrs = pShiftHours(pmStart, pmEnd);
  // Would these times give the person two shifts at once that day? (same times or overlapping)
  const pmClash = useMemo(() => {
    if (!planModal || pShiftHours(pmStart, pmEnd) <= 0) return null;
    return findShiftClash(planned, {
      userId: planModal.userId, date: planModal.date, startTime: pmStart, endTime: pmEnd, ignoreId: planModal.doc?.id,
    });
  }, [planned, planModal, pmStart, pmEnd]);

  // The person's other shifts that day — shown in the modal so you see what's already booked
  const pmSameDay = useMemo(() => {
    if (!planModal) return [];
    return (planMap[`${planModal.userId}|${planModal.date}`] || [])
      .filter(p => p.id !== planModal.doc?.id && p.status !== 'cancelled')
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [planMap, planModal]);

  // "Also add on" day chips: a day is blocked when the same times would double-book
  // the person there, or they're marked Not Available that day.
  const pmRepeatDays = useMemo(() => {
    if (!planModal) return [];
    const valid = pShiftHours(pmStart, pmEnd) > 0;
    return weekDays.map((date, i) => {
      if (date === planModal.date) return null;
      const clash = valid ? findShiftClash(planned, { userId: planModal.userId, date, startTime: pmStart, endTime: pmEnd }) : null;
      const avail = getAvailStatus(availMap, planModal.userId, date, pmStart, pmEnd);
      const why = clash ? `Already has ${pFmt12(clash.shift.startTime)}–${pFmt12(clash.shift.endTime)} that day`
        : avail === 'unavailable' ? `${planModal.userName} is not available on ${PLAN_DAYS_FULL[i]}s` : '';
      return {
        date, i, why,
        blocked: !valid || !!clash || avail === 'unavailable',
        tag: clash ? 'booked' : avail === 'unavailable' ? 'not avail' : avail === 'partial' ? 'limited' : pFmtDate(date).split(' ')[1],
      };
    }).filter(Boolean);
  }, [planModal, planned, availMap, weekDays, pmStart, pmEnd]);
  const pmRepeatDates = pmRepeatDays.filter(d => !d.blocked && pmRepeat.includes(d.date)).map(d => d.date);

  // Week-hours impact of saving (the shift being edited is swapped out, copies counted in)
  const pmWeekBefore = planModal ? (empPlanHours[planModal.userId] || 0) : 0;
  const pmEditingHrs = planModal?.doc && planModal.doc.status !== 'cancelled'
    ? pShiftHours(planModal.doc.startTime, planModal.doc.endTime) : 0;
  const pmWeekAfter = pmWeekBefore - pmEditingHrs + pmHrs * (1 + pmRepeatDates.length);

  // Shifts already double-booked in the data (from before this check existed) — flagged on the grid
  const doubleBookedIds = useMemo(() => {
    const ids = new Set();
    planned.forEach(p => {
      if (p.status === 'cancelled') return;
      if (findShiftClash(planned, { ...p, ignoreId: p.id })) ids.add(p.id);
    });
    return ids;
  }, [planned]);

  // Esc closes the shift modal / the Clear week confirm
  useEffect(() => {
    if (!planModal && !clearModal) return undefined;
    const onKey = e => {
      if (e.key !== 'Escape') return;
      if (planModal && !pmSaving) setPlanModal(null);
      if (clearModal && !clearBusy) setClearModal(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [planModal, pmSaving, clearModal, clearBusy]);

  useEffect(() => () => { clearTimeout(toastTimerRef.current); clearTimeout(teamSaveTimer.current); }, []);

  // Position count breakdown per day date string
  const dayPositionMap = useMemo(() => {
    const m = {};
    planned.forEach(p => {
      if (p.status === 'cancelled') return;
      if (!m[p.date]) m[p.date] = {};
      m[p.date][p.position] = (m[p.date][p.position] || 0) + 1;
    });
    return m;
  }, [planned]);

  // Number of scheduled-but-unavailable conflicts this week
  const weekConflictCount = useMemo(() => {
    let count = 0;
    planned.forEach(p => {
      if (p.status === 'cancelled') return;
      if (getAvailStatus(availMap, p.userId, p.date, p.startTime, p.endTime) === 'unavailable') count++;
    });
    return count;
  }, [planned, availMap]);

  // Per-employee available-day score (null = not set, 0-7 = days not-unavailable)
  const empAvailScore = useMemo(() => {
    const m = {};
    allStaff.forEach(emp => {
      const days = availMap[emp.uid]?.days;
      if (!days) { m[emp.uid] = null; return; }
      m[emp.uid] = PLAN_DAYS_FULL.filter(d => days[d]?.type !== 'unavailable').length;
    });
    return m;
  }, [allStaff, availMap]);

  // Sorted version of visiblePlanStaff
  const sortedVisiblePlanStaff = useMemo(() => {
    const list = [...visiblePlanStaff]; // already in team order
    if (planSortMode === 'hours') list.sort((a, b) => (empPlanHours[b.uid]||0) - (empPlanHours[a.uid]||0));
    else if (planSortMode === 'avail') list.sort((a, b) => (empAvailScore[b.uid] ?? -1) - (empAvailScore[a.uid] ?? -1));
    else if (planSortMode === 'name') list.sort((a, b) => (a.name||'').localeCompare(b.name||''));
    // inactive rows always sit at the bottom
    return [...list.filter(s => !team.inactive[s.uid]), ...list.filter(s => team.inactive[s.uid])];
  }, [visiblePlanStaff, planSortMode, empPlanHours, empAvailScore, team.inactive]);

  // Who the Preview (and its WhatsApp message) covers: the current View filter, scheduled people only
  const previewStaff = useMemo(() => sortedVisiblePlanStaff.filter(emp =>
    weekDays.some(d => (planMap[`${emp.uid}|${d}`]||[]).length > 0)
  ), [sortedVisiblePlanStaff, weekDays, planMap]);

  // Employees with availability set but zero shifts this week
  const unscheduledCount = useMemo(() => {
    return visiblePlanStaff.filter(e => !team.inactive[e.uid] && availMap[e.uid]?.days && !(empPlanHours[e.uid] > 0)).length;
  }, [visiblePlanStaff, availMap, empPlanHours, team.inactive]);

  const visibleAvailStaff = useMemo(() => {
    let list = activeStaff.filter(s => ALL_STAFF_ROLES.includes(s.role));
    if (availFilterRole !== 'all') {
      if (availFilterRole === 'manager') list = list.filter(s => ['manager','admin','owner'].includes(s.role));
      else list = list.filter(s => s.role === 'employee');
    }
    if (availSearchQ.trim()) {
      const q = availSearchQ.toLowerCase();
      list = list.filter(s => (s.name||'').toLowerCase().includes(q) || (s.email||'').toLowerCase().includes(q));
    }
    return list;
  }, [activeStaff, availFilterRole, availSearchQ]);

  const availDayCounts = useMemo(() => {
    const counts = {};
    PLAN_DAYS_FULL.forEach(day => {
      counts[day] = { available: 0, partial: 0, unavailable: 0, unknown: 0 };
    });
    activeStaff.forEach(emp => {
      PLAN_DAYS_FULL.forEach(day => {
        const d = availMap[emp.uid]?.days?.[day];
        const type = d?.type || 'unknown';
        counts[day][type] = (counts[day][type] || 0) + 1;
      });
    });
    return counts;
  }, [activeStaff, availMap]);

  useEffect(() => {
    if (loading) return;
    if (!user || !MANAGER_ROLES.includes(role)) { navigate('/'); return; }
  }, [user, role, loading, navigate]);

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const getDateBounds = useCallback(() => {
    const n = new Date();
    let start, end;
    if (dateRange === 'today') {
      start = new Date(n.getFullYear(), n.getMonth(), n.getDate());
      end = new Date(start); end.setDate(end.getDate() + 1);
    } else if (dateRange === 'week') {
      const day = n.getDay();
      const diffToMon = day === 0 ? -6 : 1 - day; // roll to Monday
      start = new Date(n.getFullYear(), n.getMonth(), n.getDate() + diffToMon);
      end = new Date(start); end.setDate(end.getDate() + 7);
    } else if (dateRange === 'month') {
      start = new Date(n.getFullYear(), n.getMonth(), 1);
      end = new Date(n.getFullYear(), n.getMonth() + 1, 1);
    } else {
      // a date-only string parses as UTC midnight (7 PM the day before here) — read it as local midnight
      start = customStart ? new Date(customStart + 'T00:00:00') : new Date(n.getFullYear(), n.getMonth(), n.getDate() - 30);
      end = customEnd ? new Date(customEnd + 'T23:59:59') : new Date();
    }
    return { start, end };
  }, [dateRange, customStart, customEnd]);

  const loadShifts = useCallback(async () => {
    if (!user) return;
    setLoadingData(true);
    try {
      const { start, end } = getDateBounds();
      const q = query(
        collection(db, 'shifts'),
        where('clockIn', '>=', Timestamp.fromDate(start)),
        where('clockIn', '<=', Timestamp.fromDate(end)),
        orderBy('clockIn', 'desc')
      );
      const snap = await getDocs(q);
      const list = [];
      const empMap = new Map();
      snap.forEach((d) => {
        const data = { id: d.id, ...d.data() };
        list.push(data);
        if (!isDeleted(data) && !empMap.has(data.userId)) {
          empMap.set(data.userId, { id: data.userId, name: data.userName, email: data.userEmail });
        }
      });
      setShifts(list);
      shiftEmployeesRef.current = [...empMap.values()];
      setEmployees(staffOnly(shiftEmployeesRef.current, allStaffRef.current));
    } catch (err) {
      console.error('Failed to load shifts:', err);
    }
    setLoadingData(false);
  }, [user, getDateBounds]);

  useEffect(() => { loadShifts(); }, [loadShifts]);

  // Assigned shifts in the Hours & Reports period — what anomalies compare clock records with.
  // Re-read when the period changes or when coming back to the Dashboard (the plan may have changed).
  const loadRangePlanned = useCallback(async () => {
    if (!user || !canViewHours) return;
    try {
      const { start, end } = getDateBounds();
      const from = pToISO(start), to = pToISO(new Date(+end - 1));
      const snap = await getDocs(query(collection(db, 'scheduledShifts'), where('date', '>=', from), where('date', '<=', to)));
      const list = [];
      snap.forEach(d => {
        const p = { id: d.id, ...d.data() };
        // only Mon–Sun planner weeks, like the planner shows (older Sunday-based docs are ignored)
        if (p.date >= from && p.date <= to && p.weekStart === pGetWeekMonday(new Date(`${p.date}T00:00:00`))) list.push(p);
      });
      setRangePlanned(list);
    } catch (err) { console.error('loadRangePlanned', err); }
  }, [user, canViewHours, getDateBounds]);
  useEffect(() => { if (dashView === 'dashboard') loadRangePlanned(); }, [loadRangePlanned, dashView]);

  // The staff list only filters the employee dropdown, so when it arrives re-filter
  // what's loaded instead of re-reading every shift in the range. (It used to be a
  // dependency of loadShifts, so each visit read the whole range twice.)
  useEffect(() => {
    allStaffRef.current = allStaff;
    setEmployees(staffOnly(shiftEmployeesRef.current, allStaff));
  }, [allStaff]);

  const loadStaff = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = [];
      snap.forEach((d) => {
        const data = { uid: d.id, ...d.data() };
        if (STAFF_ROLES.includes(data.role)) list.push(data);
      });
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAllStaff(list);
    } catch (err) {
      console.error('Failed to load staff:', err);
    }
  }, []);

  const loadActiveShifts = useCallback(async () => {
    try {
      const q = query(
        collection(db, 'shifts'),
        where('status', 'in', ['active', 'on-break'])
      );
      const snap = await getDocs(q);
      const map = {};
      snap.forEach((d) => { const data = { id: d.id, ...d.data() }; map[data.userId] = data; });
      setActiveShiftsMap(map);
    } catch (err) {
      console.error('Failed to load active shifts:', err);
    }
  }, []);

  // ── Load labor rates ──
  const loadLaborRates = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'laborRates'));
      const m = {};
      snap.forEach(d => { m[d.id] = d.data().hourlyRate || 0; });
      setLaborRates(m);
    } catch (err) { console.error('Failed to load labor rates:', err); }
  }, []);

  // ── Load swap requests ──
  const loadSwapRequests = useCallback(async () => {
    try {
      const q = query(collection(db, 'shiftSwaps'), where('status', '==', 'pending'));
      const snap = await getDocs(q);
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ta = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const tb = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return tb - ta;
      });
      setSwapRequests(list);
    } catch (err) { console.error('Failed to load swap requests:', err); }
  }, []);

  useEffect(() => { loadStaff(); loadActiveShifts(); loadLaborRates(); loadSwapRequests(); }, [loadStaff, loadActiveShifts, loadLaborRates, loadSwapRequests]);

  // Load pending hour-change requests
  const loadPendingRequests = useCallback(async () => {
    try {
      const q = query(
        collection(db, 'shiftRequests'),
        where('status', '==', 'pending')
      );
      const snap = await getDocs(q);
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      // Sort client-side — avoids needing a composite Firestore index
      list.sort((a, b) => {
        const ta = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const tb = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return ta - tb;
      });
      setPendingRequests(list);
    } catch (err) {
      console.error('Failed to load shift requests:', err);
      alert('Could not load pending requests. Check Firestore rules or console for details.');
    }
  }, []);

  useEffect(() => { loadPendingRequests(); }, [loadPendingRequests]);

  // Each pending add-shift / hour-change request: the person's other records the new times would
  // sit on top of (shown on the card, and replaced when it's approved)
  useEffect(() => {
    const adds = pendingRequests.filter(r => r.type !== 'delete-shift' && r.requestedClockIn && r.requestedClockOut);
    if (!adds.length) { setReqOverlaps({}); return undefined; }
    let cancelled = false;
    (async () => {
      const out = {};
      for (const r of adds) {
        const start = toDate(r.requestedClockIn), end = toDate(r.requestedClockOut);
        if (!start || !end) continue;
        try { out[r.id] = (await fetchRecordOverlaps(r.userId, start, end)).filter(x => x.id !== r.shiftId); }
        catch (err) { console.error('request overlaps', err); }
      }
      if (!cancelled) setReqOverlaps(out);
    })();
    return () => { cancelled = true; };
  }, [pendingRequests]);

  // Manual add: a changed person/time needs a fresh overlap check
  useEffect(() => { setAddOverlaps([]); }, [editEmployee, editClockIn, editClockOut]);

  const loadRequestHistory = useCallback(async () => {
    if (!canApprove) return;
    setLoadingHistory(true);
    try {
      const q = query(
        collection(db, 'shiftRequests'),
        where('status', 'in', ['approved', 'rejected', 'cancelled', 'superseded'])
      );
      const snap = await getDocs(q);
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ta = a.reviewedAt?.toDate ? a.reviewedAt.toDate() : new Date(a.reviewedAt || 0);
        const tb = b.reviewedAt?.toDate ? b.reviewedAt.toDate() : new Date(b.reviewedAt || 0);
        return tb - ta; // newest first
      });
      setReqHistory(list);
    } catch (err) {
      console.error('Failed to load request history:', err);
    }
    setLoadingHistory(false);
  }, [canApprove]);

  // ── Planner data loaders ──
  const loadPlanned = useCallback(async () => {
    setPlanLoading(true);
    try {
      const q = query(collection(db,'scheduledShifts'), where('weekStart','==',weekStart));
      const snap = await getDocs(q);
      if (weekStartRef.current !== weekStart) return; // already moved on to another week
      const list = []; snap.forEach(d => list.push({id:d.id,...d.data()}));
      setPlanned(list);
    } catch(err) { console.error('loadPlanned',err); }
    setPlanLoading(false);
  }, [weekStart]);

  const loadActualMap = useCallback(async () => {
    // a future week can't have clock-ins yet — skip the read
    if (weekStart > pGetWeekMonday(new Date())) { setActualMap({}); return; }
    try {
      const mon = new Date(weekStart+'T00:00:00');
      const sun = new Date(pAddDays(weekStart,6)+'T23:59:59');
      const q = query(collection(db,'shifts'),
        where('clockIn','>=',Timestamp.fromDate(mon)),
        where('clockIn','<=',Timestamp.fromDate(sun)));
      const snap = await getDocs(q);
      if (weekStartRef.current !== weekStart) return;
      const m = {};
      snap.forEach(d => {
        const s={id:d.id,...d.data()};
        if(isDeleted(s)) return;
        const date = s.clockIn?.toDate ? pToISO(s.clockIn.toDate()) : '';
        if(date) m[`${s.userId}|${date}`]=s.status;
      });
      setActualMap(m);
    } catch(err) { console.error('loadActualMap',err); }
  }, [weekStart]);

  // Switching weeks: drop the old week in the same render as the new dates, so its shifts,
  // counts and alerts never show (even for a frame) under the wrong week
  const goToWeek = (ws) => {
    if (ws === weekStartRef.current) return;
    setPlanned([]);
    setActualMap({});
    setPlanLoading(true);
    setWeekStart(ws);
  };
  useEffect(() => { loadPlanned(); loadActualMap(); }, [loadPlanned, loadActualMap]);

  // Planner moved off this week → fetch just today's plan + clock-ins so the Dashboard stays on today
  useEffect(() => {
    if (viewingThisWeek) { setTodayData(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const today = pToISO(now);
        const [planSnap, clockSnap] = await Promise.all([
          getDocs(query(collection(db,'scheduledShifts'), where('date','==',today))),
          getDocs(query(collection(db,'shifts'),
            where('clockIn','>=',Timestamp.fromDate(new Date(today+'T00:00:00'))),
            where('clockIn','<=',Timestamp.fromDate(new Date(today+'T23:59:59'))))),
        ]);
        if (cancelled) return;
        const thisMonday = pGetWeekMonday(now);
        const plan = [];
        planSnap.forEach(d => { const p = {id:d.id,...d.data()}; if (p.weekStart === thisMonday) plan.push(p); });
        const actual = {};
        clockSnap.forEach(d => { const s = d.data(); if (!isDeleted(s)) actual[`${s.userId}|${today}`] = s.status; });
        setTodayData({ planned: plan, actual });
      } catch(err) { console.error('loadToday',err); }
    })();
    return () => { cancelled = true; };
  }, [viewingThisWeek]);

  const loadAvailability = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'employeeAvailability'));
      const map = {};
      snap.forEach(d => { map[d.id] = d.data(); });
      setAvailMap(map);
    } catch(err) { console.error('loadAvailability', err); }
  }, []);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);
  useEffect(() => { loadTeamSettings().then(t => { savedOrderRef.current = t.order; setTeam(t); }); }, []);

  const openAvailModal = (e, emp) => {
    e.stopPropagation();
    // Pre-populate from existing data or default to all available
    const existing = availMap[emp.uid];
    const days = {};
    PLAN_DAYS_FULL.forEach(day => {
      days[day] = existing?.days?.[day] ? { ...existing.days[day] } : { type: 'available' };
    });
    setAvailDays(days);
    setAvailModal({ userId: emp.uid, userName: emp.name || emp.email });
  };

  const saveAvailModal = async () => {
    if (!availModal || availSaving) return;
    setAvailSaving(true);
    try {
      await setDoc(doc(db, 'employeeAvailability', availModal.userId), {
        days: availDays,
        updatedAt: Timestamp.now(),
        updatedBy: user.uid,
        updatedByName: user.displayName || user.email,
      });
      setAvailMap(prev => ({
        ...prev,
        [availModal.userId]: { days: availDays, updatedAt: Timestamp.now() },
      }));
      showPlanToast(`Availability saved for ${availModal.userName} ✓`);
      setAvailModal(null);
    } catch(err) { console.error('saveAvailModal', err); showPlanToast('Error saving availability'); }
    setAvailSaving(false);
  };

  // Save a single day's availability immediately (used by inline grid editing)
  const saveAvailDay = async (userId, dayName, type, startTime, endTime) => {
    const cellKey = `${userId}|${dayName}`;
    setAvailCellSaving(cellKey);
    try {
      const existing = availMap[userId]?.days || {};
      const newDays = {
        ...Object.fromEntries(PLAN_DAYS_FULL.map(d => [d, existing[d] || { type: 'available' }])),
        [dayName]: type === 'partial' ? { type, startTime: startTime || '09:00', endTime: endTime || '17:00' } : { type },
      };
      await setDoc(doc(db, 'employeeAvailability', userId), {
        days: newDays,
        updatedAt: Timestamp.now(),
        updatedBy: user.uid,
        updatedByName: user.displayName || user.email,
      });
      setAvailMap(prev => ({
        ...prev,
        [userId]: { ...prev[userId], days: newDays },
      }));
      showPlanToast('Availability updated ✓');
    } catch(err) { console.error('saveAvailDay', err); showPlanToast('Error saving'); }
    setAvailCellSaving(null);
    setAvailInlineEdit(null);
  };

  // Bulk: set all employees to available for all days
  const bulkSetAllAvailable = async () => {
    if (!window.confirm('Set ALL employees as Available every day?')) return;
    setAvailBulkSaving(true);
    try {
      const allAvailDays = Object.fromEntries(PLAN_DAYS_FULL.map(d => [d, { type: 'available' }]));
      await Promise.all(activeStaff.map(emp =>
        setDoc(doc(db, 'employeeAvailability', emp.uid), {
          days: allAvailDays,
          updatedAt: Timestamp.now(),
          updatedBy: user.uid,
          updatedByName: user.displayName || user.email,
        })
      ));
      const newMap = {};
      activeStaff.forEach(emp => { newMap[emp.uid] = { days: allAvailDays }; });
      setAvailMap(prev => ({ ...prev, ...newMap }));
      showPlanToast(`All ${activeStaff.length} employees set to Available ✓`);
    } catch(err) { console.error('bulkSetAllAvailable', err); showPlanToast('Error'); }
    setAvailBulkSaving(false);
  };

  // `action` adds a button to the toast (e.g. Undo) and keeps it up longer
  const showPlanToast = (msg, action) => {
    clearTimeout(toastTimerRef.current);
    setPlanToast({ msg, action });
    toastTimerRef.current = setTimeout(() => setPlanToast(null), action ? (action.ms || 6000) : 3000);
  };

  const openPlanAdd = (userId, userName, date) => {
    if(!canEdit||planLoading) return; // the double-booking check needs this week's shifts loaded
    if(team.inactive[userId]) return;  // inactive people can't be given new shifts
    // Auto-suggest times from partial availability window
    const dayName = pDayName(date);
    const dayAvail = availMap[userId]?.days?.[dayName];
    let start = (dayAvail?.type === 'partial' && dayAvail.startTime) ? dayAvail.startTime : '09:00';
    let end   = (dayAvail?.type === 'partial' && dayAvail.endTime)   ? dayAvail.endTime   : '17:00';
    // A 2nd shift that day: start from the first template that fits around what's
    // already booked, rather than opening on a clash
    if (findShiftClash(planned, { userId, date, startTime: start, endTime: end })) {
      const fit = SHIFT_TEMPLATES.find(t => !findShiftClash(planned, { userId, date, startTime: t.startTime, endTime: t.endTime }));
      if (fit) { start = fit.startTime; end = fit.endTime; }
    }
    setPmStart(start); setPmEnd(end); setPmPosition('Cook'); setPmNotes('');
    setPmAvailOverride(false); setPmRepeat([]); setPmConfirmDel(false);
    setPlanModal({mode:'add',userId,userName,date});
  };
  const openPlanEdit = (e, planDoc) => {
    e?.stopPropagation(); if(!canEdit) return;
    setPmStart(planDoc.startTime); setPmEnd(planDoc.endTime);
    setPmPosition(planDoc.position||'Cook'); setPmNotes(planDoc.notes||'');
    setPmAvailOverride(false); setPmRepeat([]); setPmConfirmDel(false);
    setPlanModal({mode:'edit',userId:planDoc.userId,userName:planDoc.userName,date:planDoc.date,doc:planDoc});
  };
  const applyPlanTemplate = (label) => {
    const t = SHIFT_TEMPLATES.find(t=>t.label===label);
    if(t){setPmStart(t.startTime);setPmEnd(t.endTime);}
  };
  const savePlanModal = async () => {
    if(!pmStart||!pmEnd||pmSaving) return;
    if(pShiftHours(pmStart,pmEnd)<=0){showPlanToast('End time must be after start'); return;}
    if(pmClash){showPlanToast(`${planModal.userName} already has ${pFmt12(pmClash.shift.startTime)}–${pFmt12(pmClash.shift.endTime)} that day`); return;}
    if(pmAvailStatus==='unavailable'&&!pmAvailOverride) return;
    setPmSaving(true);
    try {
      const empData = allStaff.find(s=>s.uid===planModal.userId)||{};
      const now = Timestamp.now();
      const payload = {
        userId:planModal.userId, userName:planModal.userName,
        userRole:empData.role||'', userEmail:empData.email||'',
        date:planModal.date, weekStart,
        startTime:pmStart, endTime:pmEnd, position:pmPosition,
        notes:pmNotes.trim(), status:'scheduled', updatedAt:now,
      };
      const created = { createdBy:user.uid, createdAt:now };
      // Same shift on the other picked days (days that would double-book are never in pmRepeatDates)
      const copies = pmRepeatDates.map(date => ({ ...payload, ...created, date }));
      const adds = planModal.mode==='add' ? [{ ...payload, ...created }, ...copies] : copies;
      if(planModal.mode==='edit') await updateDoc(doc(db,'scheduledShifts',planModal.doc.id),payload);
      const refs = await Promise.all(adds.map(s=>addDoc(collection(db,'scheduledShifts'),s)));
      // Patch the week locally instead of re-reading it from Firestore
      setPlanned(prev => [
        ...prev.map(p => planModal.mode==='edit' && p.id===planModal.doc.id ? { ...p, ...payload } : p),
        ...adds.map((s,i) => ({ id:refs[i].id, ...s })),
      ]);
      const more = copies.length ? ` + ${copies.length} more day${copies.length>1?'s':''}` : '';
      showPlanToast(`${planModal.mode==='add'?'Shift added':'Shift updated'}${more} ✓`);
      setPlanModal(null);
    } catch(err){console.error('savePlanModal',err);showPlanToast('Error saving');loadPlanned();}
    setPmSaving(false);
  };
  // Deletes straight away and offers Undo, so there's no confirm dialog on the grid's ×
  const deletePlanShift = async (e, planDoc) => {
    e?.stopPropagation();
    if(!canEdit) return false;
    try {
      await deleteDoc(doc(db,'scheduledShifts',planDoc.id));
      setPlanned(prev => prev.filter(p => p.id !== planDoc.id));
      showPlanToast(`Removed ${planDoc.userName}'s ${pFmt12(planDoc.startTime)}–${pFmt12(planDoc.endTime)} shift`,
        { label: 'Undo', run: () => restorePlanShift(planDoc) });
      return true;
    } catch(err){console.error('deletePlan',err);showPlanToast('Error removing shift');return false;}
  };
  const deleteFromModal = async () => {
    if(pmSaving||!planModal?.doc) return;
    setPmSaving(true);
    const ok = await deletePlanShift(null, planModal.doc);
    setPmSaving(false);
    if(ok) setPlanModal(null);
  };
  const restorePlanShift = (planDoc) => restorePlanShifts([planDoc]);
  // Undo for deletes: puts the docs back under their old ids. Any whose time was booked
  // again since are skipped, so Undo never creates a double-booking.
  const restorePlanShifts = async (docs) => {
    const ok = docs.filter(p => !findShiftClash(plannedRef.current, { ...p, ignoreId: p.id }));
    const skipped = docs.length - ok.length;
    if(!ok.length){ showPlanToast("Can't undo — those times have been booked again"); return; }
    try {
      for(let i=0;i<ok.length;i+=400){ // a batch takes up to 500 writes
        const batch = writeBatch(db);
        ok.slice(i,i+400).forEach(({ id, ...data }) => batch.set(doc(db,'scheduledShifts',id), data));
        await batch.commit();
      }
      const back = ok.filter(p => p.weekStart===weekStartRef.current);
      setPlanned(prev => [...prev, ...back.filter(p => !prev.some(x => x.id===p.id))]);
      showPlanToast(ok.length===1 && !skipped ? 'Shift restored ✓'
        : `Restored ${ok.length} shift${ok.length>1?'s':''} ✓${skipped?` · ${skipped} skipped (time booked again)`:''}`);
    } catch(err){console.error('restorePlan',err);showPlanToast('Error restoring shifts');}
  };

  // ── Clear week ──
  const clearTargets = (opts) => clearWeekTargets(planned, {
    ...opts, inViewIds: new Set(visiblePlanStaff.map(s => s.uid)), today: todayISO,
  });
  const openClearWeek = () => {
    if(!canEdit||planLoading||!planned.length) return;
    // on the current week, days that already happened are kept by default
    const pastDays = weekDays.filter(d => d < todayISO).length;
    setClearModal({ scope: planFilterRole==='all' ? 'all' : 'view', keepPast: pastDays > 0 && pastDays < 7 });
  };
  const clearWeek = async () => {
    if(!clearModal||clearBusy) return;
    const targets = clearTargets(clearModal);
    if(!targets.length) return;
    setClearBusy(true);
    try {
      for(let i=0;i<targets.length;i+=400){
        const batch = writeBatch(db);
        targets.slice(i,i+400).forEach(p => batch.delete(doc(db,'scheduledShifts',p.id)));
        await batch.commit();
      }
      const gone = new Set(targets.map(p => p.id));
      setPlanned(prev => prev.filter(p => !gone.has(p.id)));
      setClearModal(null);
      showPlanToast(`Cleared ${targets.length} shift${targets.length>1?'s':''} from ${weekRangeLabel(weekStart)}`,
        { label: 'Undo', run: () => restorePlanShifts(targets), ms: 10000 });
    } catch(err){console.error('clearWeek',err);showPlanToast('Error clearing the week');loadPlanned();}
    setClearBusy(false);
  };

  // ── Share the week (Preview) ──
  const weekShareText = () => weekScheduleText({ weekDays, staff: previewStaff, planMap, dayNames: PLAN_DAYS, layout: shareLayout });
  const pickShareLayout = (layout) => {
    setShareLayout(layout);
    try { localStorage.setItem('sp_share_layout', layout); } catch { /* private mode — just not remembered */ }
  };
  // Opens WhatsApp with the schedule pre-filled — you pick the staff group or a contact
  const shareWeekWhatsApp = () => {
    if(!previewStaff.length) return;
    window.open(whatsappUrl(weekShareText()), '_blank', 'noopener,noreferrer');
  };
  const copyWeekText = async () => {
    try { await navigator.clipboard.writeText(weekShareText()); showPlanToast('Schedule copied ✓'); }
    catch { showPlanToast('Could not copy — try Share on WhatsApp'); }
  };
  // Just this person's shifts; opens their chat directly when their profile has a phone number
  const sharePersonWhatsApp = (emp) => {
    const text = personScheduleText({ emp, weekDays, planMap, dayNames: PLAN_DAYS });
    window.open(whatsappUrl(text, emp.phone), '_blank', 'noopener,noreferrer');
  };
  // ── 👥 Team: order + deactivate ──
  // A new order shows at once and is saved shortly after the last move (one write per burst)
  const saveTeamOrderNow = async () => {
    const order = pendingOrderRef.current;
    if(!order) return;
    pendingOrderRef.current = null;
    try { await saveTeamOrder(order, user); savedOrderRef.current = order; setTeamSave('saved'); }
    catch(err){
      console.error('saveTeamOrder',err);
      // put the saved order back so the screen never shows an order that isn't stored
      if(!pendingOrderRef.current) setTeam(t => ({ ...t, order: savedOrderRef.current }));
      setTeamSave(err?.code === 'permission-denied' ? 'denied' : 'error');
    }
  };
  const reorderTeam = (activeIds) => {
    const order = [...activeIds, ...teamStaff.filter(s => team.inactive[s.uid]).map(s => s.uid)];
    setTeam(t => ({ ...t, order }));
    pendingOrderRef.current = order;
    setTeamSave('saving');
    clearTimeout(teamSaveTimer.current);
    teamSaveTimer.current = setTimeout(saveTeamOrderNow, 600);
  };
  const closeTeam = () => {
    clearTimeout(teamSaveTimer.current);
    saveTeamOrderNow();
    setTeamOpen(false);
  };
  // Shifts of theirs that haven't started yet, across all weeks (offered for removal on deactivate)
  const loadUpcomingFor = async (uid) => {
    try {
      const snap = await getDocs(query(collection(db,'scheduledShifts'), where('userId','==',uid)));
      const list = []; snap.forEach(d => list.push({ id:d.id, ...d.data() }));
      return upcomingShifts(list);
    } catch(err){ console.error('loadUpcoming',err); showPlanToast("Couldn't check their upcoming shifts"); return []; }
  };
  const deactivateMember = async (emp, shiftsToRemove) => {
    const name = emp.name || emp.email;
    try { await markInactive(emp.uid, user); }
    catch(err){ console.error('deactivate',err); showPlanToast(`Couldn't deactivate ${name}`); return false; }
    setTeam(t => ({ ...t, inactive: { ...t.inactive, [emp.uid]: { at: Timestamp.now(), by: user.uid, byName: user.displayName || user.email || '' } } }));
    let removed = [];
    try {
      for(let i=0;i<shiftsToRemove.length;i+=400){
        const batch = writeBatch(db);
        shiftsToRemove.slice(i,i+400).forEach(p => batch.delete(doc(db,'scheduledShifts',p.id)));
        await batch.commit();
      }
      removed = shiftsToRemove;
      const gone = new Set(removed.map(p => p.id));
      setPlanned(prev => prev.filter(p => !gone.has(p.id)));
    } catch(err){ console.error('deactivate: remove shifts',err); }
    const failed = shiftsToRemove.length - removed.length;
    showPlanToast(
      `${name} deactivated${removed.length ? ` · ${removed.length} upcoming shift${removed.length>1?'s':''} removed` : ''}${failed ? " · couldn't remove their upcoming shifts" : ''}`,
      { label: 'Undo', ms: 10000, run: () => undoDeactivate(emp, removed) },
    );
    return true;
  };
  const reactivateMember = async (emp, quiet) => {
    try { await markActive(emp.uid, user); }
    catch(err){ console.error('reactivate',err); showPlanToast(`Couldn't reactivate ${emp.name || emp.email}`); return false; }
    setTeam(t => { const inactive = { ...t.inactive }; delete inactive[emp.uid]; return { ...t, inactive }; });
    if(!quiet) showPlanToast(`${emp.name || emp.email} is active again ✓`);
    return true;
  };
  const undoDeactivate = async (emp, removed) => {
    if(!(await reactivateMember(emp, removed.length > 0))) return;
    if(removed.length) await restorePlanShifts(removed);
  };

  const copyLastWeek = async () => {
    if(!canEdit||copyBusy||planLoading) return;
    const prevStart = pAddDays(weekStart,-7);
    setCopyBusy(true);
    try {
      const q = query(collection(db,'scheduledShifts'),where('weekStart','==',prevStart));
      const snap = await getDocs(q);
      if(snap.empty){showPlanToast('No shifts in previous week');setCopyBusy(false);return;}
      const existingKeys=new Set(planned.map(p=>`${p.userId}|${p.date}`));
      const toCreate=[];
      snap.forEach(d=>{
        const p=d.data(); if(p.status==='cancelled') return;
        if(team.inactive[p.userId]) return; // don't put deactivated people back on the schedule
        const prevD=new Date(p.date+'T00:00:00');
        // remap day-of-week to this week (Mon=1…Sun=0 → offset from weekStart)
        const dow=prevD.getDay(); const offset=dow===0?6:dow-1;
        const newDate=pAddDays(weekStart,offset);
        const key=`${p.userId}|${newDate}`;
        if(existingKeys.has(key)) return;
        const copy={...p,date:newDate,weekStart,createdBy:user.uid,createdAt:Timestamp.now(),updatedAt:Timestamp.now()};
        // don't carry last week's double-bookings forward
        if(findShiftClash(toCreate,copy)) return;
        toCreate.push(copy);
      });
      await Promise.all(toCreate.map(s=>addDoc(collection(db,'scheduledShifts'),s)));
      showPlanToast(`Copied ${toCreate.length} shift(s) ✓`); loadPlanned();
    } catch(err){console.error('copyLastWeek',err);showPlanToast('Error copying');}
    setCopyBusy(false);
  };

  // ── Requests: approve / reject / dismiss duplicates ──
  const reviewStamp = () => ({ reviewedBy: user.uid, reviewedByName: user.displayName || user.email, reviewedAt: Timestamp.now() });
  // Soft delete: the record stays (audit trail, Undo, Restore) but counts no hours anywhere
  const deleteStamp = (rec, reason) => ({
    status: 'deleted', prevStatus: rec.status === 'deleted' ? (rec.prevStatus || 'completed') : rec.status,
    deletedAt: Timestamp.now(), deletedBy: user.uid, deletedByName: user.displayName || user.email || '', deleteReason: reason || '',
  });
  // Same pending request sent more than once → duplicate id → the one we keep (the newest)
  const pendingDupes = duplicatePendingRequests(pendingRequests);
  const dupGroup = r => pendingDupes.get(r.id) || r.id;

  const handleApproveRequest = async (req) => {
    if (reqBusy) return;
    setReqBusy(req.id);
    try {
      const type = req.type || 'edit';
      const newIn = toDate(req.requestedClockIn), newOut = toDate(req.requestedClockOut);
      const approver = user.displayName || 'admin';
      const batch = writeBatch(db);
      let replaced = 0;
      if (type === 'new-shift') {
        // Create the shift and retire the records it replaces (e.g. 0-minute punches made trying to fix it)
        const overlaps = reqOverlaps[req.id] || [];
        batch.set(doc(collection(db, 'shifts')), {
          userId: req.userId, userName: req.userName, userEmail: req.userEmail || '', userRole: req.userRole || 'employee',
          clockIn: Timestamp.fromDate(newIn), clockOut: Timestamp.fromDate(newOut), breaks: [],
          totalMinutes: Math.round((newOut - newIn) / 60000), date: pToISO(newIn), status: 'completed',
          notes: `Added by ${req.submittedByName || req.userName || 'staff'}, approved by ${approver} on ${new Date().toLocaleDateString()}`,
          clockedInBy: req.submittedBy || req.userId,
        });
        overlaps.forEach(r => batch.update(doc(db, 'shifts', r.id), deleteStamp(r, 'Replaced by an approved added shift')));
        replaced = overlaps.length;
      } else {
        const snap = await getDoc(doc(db, 'shifts', req.shiftId));
        if (!snap.exists()) throw Object.assign(new Error('That clock record no longer exists.'), { friendly: true });
        const rec = { id: snap.id, ...snap.data() };
        if (type === 'delete-shift') {
          batch.update(doc(db, 'shifts', rec.id), deleteStamp(rec, `Deletion approved${req.reason ? ` — ${req.reason}` : ''}`));
        } else {
          batch.update(doc(db, 'shifts', rec.id), {
            clockIn: Timestamp.fromDate(newIn), clockOut: Timestamp.fromDate(newOut),
            totalMinutes: workedMinutes(newIn, newOut, rec.breaks || []), // breaks inside the new times still don't count
            status: 'completed',
            notes: `${rec.notes ? `${rec.notes} | ` : ''}Hours corrected via approved request by ${approver} on ${new Date().toLocaleDateString()}`,
          });
          // the corrected times replace any other record they now overlap
          const overlaps = (reqOverlaps[req.id] || []).filter(r => r.id !== rec.id);
          overlaps.forEach(r => batch.update(doc(db, 'shifts', r.id), deleteStamp(r, 'Replaced by an approved hour change')));
          replaced = overlaps.length;
        }
      }
      batch.update(doc(db, 'shiftRequests', req.id), { status: 'approved', ...reviewStamp() });
      // Any duplicates of this request are closed in the same write
      const twins = pendingRequests.filter(r => r.id !== req.id && dupGroup(r) === dupGroup(req));
      twins.forEach(r => batch.update(doc(db, 'shiftRequests', r.id), { status: 'superseded', supersededBy: req.id, ...reviewStamp() }));
      await batch.commit();
      const closed = new Set([req.id, ...twins.map(r => r.id)]);
      setPendingRequests(prev => prev.filter(r => !closed.has(r.id)));
      showPlanToast(type === 'delete-shift' ? 'Deletion approved — record removed ✓'
        : type === 'new-shift' ? `Shift added${replaced ? ` · replaced ${replaced} overlapping record${replaced > 1 ? 's' : ''}` : ''} ✓`
        : `Change approved${replaced ? ` · replaced ${replaced} overlapping record${replaced > 1 ? 's' : ''}` : ''} ✓`);
      loadShifts();
      loadActiveShifts();
    } catch (err) {
      console.error('Approve request failed:', err);
      showPlanToast(err.friendly ? err.message : 'Failed to approve the request');
    }
    setReqBusy(null);
  };

  const handleRejectRequest = async (req) => {
    if (reqBusy) return;
    setReqBusy(req.id);
    try {
      await updateDoc(doc(db, 'shiftRequests', req.id), { status: 'rejected', ...reviewStamp() });
      setPendingRequests((prev) => prev.filter((r) => r.id !== req.id));
    } catch (err) {
      console.error('Reject request failed:', err);
      showPlanToast('Failed to reject the request');
    }
    setReqBusy(null);
  };

  const dismissDuplicate = async (req) => {
    if (reqBusy) return;
    setReqBusy(req.id);
    try {
      await updateDoc(doc(db, 'shiftRequests', req.id), { status: 'superseded', supersededBy: pendingDupes.get(req.id) || null, ...reviewStamp() });
      setPendingRequests((prev) => prev.filter((r) => r.id !== req.id));
      showPlanToast('Duplicate dismissed');
    } catch (err) {
      console.error('Dismiss duplicate failed:', err);
      showPlanToast('Could not dismiss it');
    }
    setReqBusy(null);
  };

  // ── Anomalies: Mark OK (or undo) — saved on the clock records, or on the assigned shift for a no-show ──
  const markAnomalyOk = async (e, ok = true) => {
    if (ackBusy) return;
    setAckBusy(e.key);
    try {
      const stamp = { at: Timestamp.now(), by: user.uid, byName: user.displayName || user.email || '' };
      const kinds = e.issues.map(i => i.kind);
      const batch = writeBatch(db);
      const recReviews = new Map(), schedReviews = new Map();
      if (e.records.length) {
        e.records.forEach(r => {
          const review = ok ? { ...stamp, kinds: [...new Set([...(r.anomalyReview?.kinds || []), ...kinds])] } : null;
          batch.update(doc(db, 'shifts', r.id), { anomalyReview: review || deleteField() });
          recReviews.set(r.id, review);
        });
      } else {
        e.scheduledDocs.forEach(p => {
          batch.update(doc(db, 'scheduledShifts', p.id), { anomalyReview: ok ? stamp : deleteField() });
          schedReviews.set(p.id, ok ? stamp : null);
        });
      }
      await batch.commit();
      const withReview = (x, review) => { const { anomalyReview, ...rest } = x; return review ? { ...rest, anomalyReview: review } : rest; };
      setShifts(prev => prev.map(x => (recReviews.has(x.id) ? withReview(x, recReviews.get(x.id)) : x)));
      setRangePlanned(prev => prev.map(x => (schedReviews.has(x.id) ? withReview(x, schedReviews.get(x.id)) : x)));
      showPlanToast(ok ? `Marked OK — ${e.userName}, ${e.dayLabel}` : 'Back in the anomalies list', ok ? {
        label: 'Undo', run: () => markAnomalyOk({ ...e, records: e.records.map(r => ({ ...r, anomalyReview: recReviews.get(r.id) })) }, false),
      } : undefined);
    } catch (err) {
      console.error('markAnomalyOk', err);
      showPlanToast('Could not save — try again');
    }
    setAckBusy(null);
  };

  // ── Delete clock records (soft) ──
  const softDeleteRecords = async (records, reason) => {
    const batch = writeBatch(db);
    const stamped = new Map(records.map(r => [r.id, { ...r, ...deleteStamp(r, reason) }]));
    records.forEach(r => batch.update(doc(db, 'shifts', r.id), deleteStamp(r, reason)));
    await batch.commit();
    setShifts(prev => prev.map(x => stamped.get(x.id) || x));
    if (records.some(r => r.status === 'active' || r.status === 'on-break')) loadActiveShifts();
  };
  const restoreRecords = async (records) => {
    const batch = writeBatch(db);
    const back = new Map();
    records.forEach(r => {
      const status = r.status === 'deleted' ? (r.prevStatus || 'completed') : r.status;
      batch.update(doc(db, 'shifts', r.id), {
        status, prevStatus: deleteField(), deletedAt: deleteField(), deletedBy: deleteField(), deletedByName: deleteField(), deleteReason: deleteField(),
      });
      const { prevStatus, deletedAt, deletedBy, deletedByName, deleteReason, ...rest } = r;
      back.set(r.id, { ...rest, status });
    });
    await batch.commit();
    setShifts(prev => prev.map(x => back.get(x.id) || x));
    if ([...back.values()].some(r => r.status === 'active' || r.status === 'on-break')) loadActiveShifts();
  };
  // Owner/admin: delete now, with Undo
  const deleteRecords = async (records, reason) => {
    try {
      await softDeleteRecords(records, reason);
      showPlanToast(records.length === 1 ? `Deleted ${records[0].userName}'s record` : `Deleted ${records.length} records`, {
        label: 'Undo', ms: 10000,
        run: async () => {
          try { await restoreRecords(records); showPlanToast('Restored ✓'); }
          catch (e) { console.error('restore', e); showPlanToast('Could not restore'); }
        },
      });
      return true;
    } catch (err) {
      console.error('Delete record failed:', err);
      showPlanToast('Could not delete the record');
      return false;
    }
  };
  const restoreDeleted = async (rec) => {
    try { await restoreRecords([rec]); showPlanToast(`Restored ${rec.userName}'s record ✓`); }
    catch (err) { console.error('restore', err); showPlanToast('Could not restore'); }
  };
  // Manager withdraws their own pending edit request
  const handleCancelManagerEdit = async (req) => {
    if (!window.confirm('Withdraw this edit request?')) return;
    setReqBusy(req.id);
    try {
      await updateDoc(doc(db, 'shiftRequests', req.id), {
        status: 'cancelled',
        reviewedBy: user.uid,
        reviewedByName: user.displayName || user.email,
        reviewedAt: Timestamp.now(),
      });
      setPendingRequests((prev) => prev.filter((r) => r.id !== req.id));
    } catch (err) {
      console.error('Cancel edit request failed:', err);
      alert('Failed to withdraw request.');
    }
    setReqBusy(null);
  };

  const handleManagerClockIn = async (staff) => {
    if (busy) return;
    setBusy(staff.uid);
    try {
      // They may have clocked themselves in since this page loaded — never start a second shift
      const openSnap = await getDocs(query(collection(db, 'shifts'),
        where('userId', '==', staff.uid), where('status', 'in', ['active', 'on-break']), limit(1)));
      if (!openSnap.empty) {
        const open = { id: openSnap.docs[0].id, ...openSnap.docs[0].data() };
        setActiveShiftsMap((prev) => ({ ...prev, [staff.uid]: open }));
        showPlanToast(`${staff.name} is already clocked in (since ${fmtTime(toDate(open.clockIn))})`);
        setBusy(null);
        return;
      }
      const nowTs = Timestamp.now();
      const ref = await addDoc(collection(db, 'shifts'), {
        userId: staff.uid,
        userName: staff.name || '(no name)',
        userEmail: staff.email,
        userRole: staff.role || 'employee',
        clockIn: nowTs,
        clockOut: null,
        breaks: [],
        totalMinutes: 0,
        date: pToISO(new Date()),
        status: 'active',
        notes: `Clocked in by ${user.displayName || 'manager'}`,
        clockedInBy: user.uid,
      });
      setActiveShiftsMap((prev) => ({
        ...prev,
        [staff.uid]: { id: ref.id, userId: staff.uid, userName: staff.name, userEmail: staff.email, clockIn: nowTs, status: 'active', breaks: [], notes: '' },
      }));
      loadShifts();
    } catch (err) {
      console.error('Manager clock-in failed:', err);
      alert('Failed to clock in employee.');
    }
    setBusy(null);
  };

  const handleManagerClockOut = async (staff) => {
    if (busy) return;
    const shift = activeShiftsMap[staff.uid];
    if (!shift) return;
    if (!window.confirm(`Clock out ${staff.name}?`)) return;
    setBusy(staff.uid);
    try {
      const clockOutTs = Timestamp.now();
      const clockInDate = shift.clockIn?.toDate ? shift.clockIn.toDate() : new Date(shift.clockIn);
      const clockOutDate = clockOutTs.toDate();
      let breakMs = 0;
      (shift.breaks || []).forEach((b) => {
        const start = b.start?.toDate ? b.start.toDate() : new Date(b.start);
        const end = b.end ? (b.end?.toDate ? b.end.toDate() : new Date(b.end)) : clockOutDate;
        breakMs += end - start;
      });
      const totalMinutes = Math.round((clockOutDate - clockInDate - breakMs) / 60000);
      await updateDoc(doc(db, 'shifts', shift.id), {
        clockOut: clockOutTs,
        status: 'completed',
        totalMinutes,
        notes: (shift.notes || '') + ` | Clocked out by ${user.displayName || 'manager'}`,
      });
      setActiveShiftsMap((prev) => { const next = { ...prev }; delete next[staff.uid]; return next; });
      loadShifts();
    } catch (err) {
      console.error('Manager clock-out failed:', err);
      alert('Failed to clock out employee.');
    }
    setBusy(null);
  };

  // Open edit modal for existing shift
  const openEditModal = (shift, startWithDelete = false) => {
    const inDate = toDate(shift.clockIn);
    const outDate = toDate(shift.clockOut);
    const toLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setEditClockIn(toLocal(inDate));
    setEditClockOut(outDate ? toLocal(outDate) : '');
    setEditEmployee('');
    setManagerEditReason('');
    setEditDelete(startWithDelete ? { reason: '' } : null);
    setEditModal(shift);
  };

  // Open modal for adding a manual shift
  const openAddShiftModal = () => {
    setEditClockIn('');
    setEditClockOut('');
    setEditEmployee(activeStaff.length > 0 ? activeStaff[0].uid : '');
    setEditDelete(null);
    setAddOverlaps([]);
    setReplaceOverlaps(true);
    setEditModal('new');
  };

  // Save edited shift
  const handleSaveEdit = async () => {
    if (editSavingRef.current) return; // a second tap before React re-renders
    if (!editClockIn) { alert('Clock-in time is required.'); return; }
    const clockInDate = new Date(editClockIn);
    const clockOutDate = editClockOut ? new Date(editClockOut) : null;
    if (clockOutDate && clockOutDate <= clockInDate) { alert('Clock-out must be after clock-in.'); return; }
    editSavingRef.current = true;
    setEditSaving(true);
    try {
      if (editModal === 'new') {
        const staff = allStaff.find((s) => s.uid === editEmployee);
        if (!staff) { alert('Select an employee.'); return; }
        if (!clockOutDate) { alert('Clock-out time is required.'); return; }
        const totalMinutes = Math.round((clockOutDate - clockInDate) / 60000);

        if (!canDirectEdit) {
          // Manager: say so if it lands on their existing records (the next tap sends it anyway)
          if (!addOverlaps.length) {
            const found = await fetchRecordOverlaps(staff.uid, clockInDate, clockOutDate);
            if (found.length) { setAddOverlaps(found); return; }
          }
          // Send for approval — sending the same shift again updates the waiting request
          const { updated } = await submitShiftRequest({
            type: 'new-shift', shiftId: null,
            userId: staff.uid, userName: staff.name || '(no name)', userEmail: staff.email, userRole: staff.role || 'employee',
            submittedBy: user.uid, submittedByName: user.displayName || user.email, submittedByRole: role,
            requestedClockIn: Timestamp.fromDate(clockInDate), requestedClockOut: Timestamp.fromDate(clockOutDate),
            totalMinutes, reason: managerEditReason.trim() || 'Manual shift entry by manager',
          });
          showPlanToast(updated ? 'Your waiting request was updated — no duplicate sent' : 'Sent for approval ✓');
          setManagerEditReason('');
          setEditModal(null);
          loadPendingRequests();
          return;
        }

        // Owner/admin: never stack a manual shift on top of existing punches without saying so
        if (!addOverlaps.length) {
          const found = await fetchRecordOverlaps(staff.uid, clockInDate, clockOutDate);
          if (found.length) { setAddOverlaps(found); setReplaceOverlaps(true); return; } // shown in the modal; the next tap saves
        }
        const batch = writeBatch(db);
        batch.set(doc(collection(db, 'shifts')), {
          userId: staff.uid,
          userName: staff.name || '(no name)',
          userEmail: staff.email,
          userRole: staff.role || 'employee',
          clockIn: Timestamp.fromDate(clockInDate),
          clockOut: Timestamp.fromDate(clockOutDate),
          breaks: [],
          totalMinutes,
          date: pToISO(clockInDate),
          status: 'completed',
          notes: `Manual entry by ${user.displayName || 'admin'}`,
          clockedInBy: user.uid,
        });
        if (replaceOverlaps) addOverlaps.forEach(r => batch.update(doc(db, 'shifts', r.id), deleteStamp(r, 'Replaced by a manual entry')));
        await batch.commit();
        showPlanToast(replaceOverlaps && addOverlaps.length
          ? `Shift added · replaced ${addOverlaps.length} overlapping record${addOverlaps.length > 1 ? 's' : ''} ✓` : 'Shift added ✓');
      } else {
        // Edit existing
        if (!canDirectEdit) {
          // Manager role: submit for admin/owner approval (re-sending updates the waiting request)
          if (!managerEditReason.trim()) { alert('Please enter a reason for this change.'); return; }
          if (!clockOutDate) { alert('Clock-out time is required.'); return; }
          if (!addOverlaps.length) {
            const found = (await fetchRecordOverlaps(editModal.userId, clockInDate, clockOutDate)).filter(r => r.id !== editModal.id);
            if (found.length) { setAddOverlaps(found); return; } // shown in the popup; the next tap sends it
          }
          const { updated } = await submitShiftRequest({
            type: 'edit', shiftId: editModal.id,
            userId: editModal.userId, userName: editModal.userName, userEmail: editModal.userEmail,
            submittedBy: user.uid, submittedByName: user.displayName || user.email, submittedByRole: role,
            originalClockIn: editModal.clockIn, originalClockOut: editModal.clockOut || null,
            requestedClockIn: Timestamp.fromDate(clockInDate), requestedClockOut: Timestamp.fromDate(clockOutDate),
            reason: managerEditReason.trim(),
          });
          showPlanToast(updated ? 'Your waiting request was updated — no duplicate sent' : 'Sent for approval ✓');
          setManagerEditReason('');
          setEditModal(null);
          loadPendingRequests();
          return;
        }
        const updates = {
          clockIn: Timestamp.fromDate(clockInDate),
          notes: (editModal.notes || '') + ` | Corrected by ${user.displayName || 'admin'}`,
        };
        if (clockOutDate) {
          updates.clockOut = Timestamp.fromDate(clockOutDate);
          // breaks taken inside the corrected times still don't count as worked
          updates.totalMinutes = workedMinutes(clockInDate, clockOutDate, editModal.breaks || []);
          updates.status = 'completed';
        }
        // A correction that lands on their other records would count those hours twice — say so first
        if (!addOverlaps.length) {
          const found = (await fetchRecordOverlaps(editModal.userId, clockInDate, clockOutDate || new Date())).filter(r => r.id !== editModal.id);
          if (found.length) { setAddOverlaps(found); setReplaceOverlaps(true); return; } // the next tap saves
        }
        const batch = writeBatch(db);
        batch.update(doc(db, 'shifts', editModal.id), updates);
        if (replaceOverlaps) addOverlaps.forEach(r => batch.update(doc(db, 'shifts', r.id), deleteStamp(r, 'Replaced by a corrected record')));
        await batch.commit();
        if (replaceOverlaps && addOverlaps.length) showPlanToast(`Saved · replaced ${addOverlaps.length} overlapping record${addOverlaps.length > 1 ? 's' : ''} ✓`);
      }
      setEditModal(null);
      loadShifts();
      loadActiveShifts();
    } catch (err) {
      console.error('Save edit failed:', err);
      alert(err?.code === 'already-pending' ? 'A request for this is already waiting for approval.' : 'Failed to save changes.');
    } finally {
      setEditSaving(false);
      editSavingRef.current = false;
    }
  };

  // Delete from the edit modal: owner/admin delete (with Undo); managers send it for approval
  const confirmEditDelete = async () => {
    if (!editModal || editModal === 'new' || editSavingRef.current) return;
    const reason = (editDelete?.reason || '').trim();
    if (!canDeleteRecords) return;
    if (deleteNeedsReason && !reason) { setEditDelete(d => ({ ...d, error: 'Please say why this record should be deleted.' })); return; }
    editSavingRef.current = true;
    setEditSaving(true);
    const ok = await deleteRecords([editModal], reason);
    setEditSaving(false);
    editSavingRef.current = false;
    if (ok) { setEditDelete(null); setEditModal(null); }
  };

  // ── Overtime Detection (must be before early return) ──
  const OVERTIME_THRESHOLD = 40;
  const overtimeAlerts = useMemo(() => {
    const alerts = [];
    Object.entries(empPlanHours).forEach(([userId, hours]) => {
      const emp = allStaff.find(s => s.uid === userId);
      if (!emp) return;
      if (hours >= OVERTIME_THRESHOLD) {
        alerts.push({ userId, name: emp.name, hours, type: 'over', msg: `${emp.name} is at ${hours.toFixed(1)}h — over ${OVERTIME_THRESHOLD}h overtime limit` });
      } else if (hours >= OVERTIME_THRESHOLD - 4) {
        alerts.push({ userId, name: emp.name, hours, type: 'near', msg: `${emp.name} is at ${hours.toFixed(1)}h — ${(OVERTIME_THRESHOLD - hours).toFixed(1)}h until overtime` });
      }
    });
    return alerts;
  }, [empPlanHours, allStaff]);

  // ── Late Clock-in Detection (must be before early return) ──
  const LATE_THRESHOLD_MIN = 15;
  const lateAlerts = useMemo(() => {
    const alerts = [];
    const nowTime = now;
    todayShown.forEach(s => {
      const clockedStatus = todayActualMap[`${s.userId}|${s.date}`];
      if (clockedStatus) return;
      const [sh, sm] = (s.startTime || '').split(':').map(Number);
      if (isNaN(sh)) return;
      const scheduledStart = new Date(nowTime);
      scheduledStart.setHours(sh, sm, 0, 0);
      const diffMin = (nowTime - scheduledStart) / 60000;
      if (diffMin >= LATE_THRESHOLD_MIN) {
        alerts.push({ userId: s.userId, name: s.userName, position: s.position, scheduledTime: pFmt12(s.startTime), lateMin: Math.round(diffMin) });
      }
    });
    return alerts;
  }, [todayShown, todayActualMap, now]);

  if (loading || !user || !MANAGER_ROLES.includes(role)) return null;

  // Roster: active people, plus anyone inactive who's still clocked in (so they can be clocked out)
  const rosterStaff = teamStaff.filter(s => !team.inactive[s.uid] || activeShiftsMap[s.uid]);

  // Deleted clock records are kept (audit / restore) but only shown in the "Deleted" view
  const liveShifts = shifts.filter(s => !isDeleted(s));
  const deletedShifts = shifts.filter(isDeleted);
  const timesheetIssues = findTimesheetIssues(liveShifts, now);

  // ── Anomalies: clock records vs the assigned shifts of this period (±1h is fine) ──
  const inScope = x => filterEmployee === 'all' || x.userId === filterEmployee;
  const anomalies = findAnomalies({
    records: liveShifts.filter(inScope),
    scheduled: rangePlanned.filter(inScope),
    scheduledDays: new Set(rangePlanned.map(p => p.date)), // days the planner was used at all
    now,
  });
  const openAnomalies = anomalies.entries.filter(e => !e.reviewed);
  const reviewedAnomalies = anomalies.entries.filter(e => e.reviewed);
  const anomalyGroupOf = id => ANOMALY_GROUPS.find(g => g.id === id);
  const anomalyMatches = (e, id) => id === 'all' || e.issues.some(i => anomalyGroupOf(id).kinds.includes(i.kind));
  const anomalyList = (showReviewed ? reviewedAnomalies : openAnomalies).filter(e => anomalyMatches(e, anomalyFilter));
  const anomalyCountByUser = openAnomalies.reduce((m, e) => { m[e.userId] = (m[e.userId] || 0) + 1; return m; }, {});

  const filtered = (showDeleted ? deletedShifts : liveShifts).filter((s) => {
    if (onlyIssues && !showDeleted && !timesheetIssues.has(s.id)) return false;
    if (filterEmployee !== 'all' && s.userId !== filterEmployee) return false;
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    return true;
  });

  const activeNow = filtered.filter((s) => s.status === 'active' || s.status === 'on-break')
    .sort((a, b) => rankOf(a.userId) - rankOf(b.userId));
  const completed = filtered.filter((s) => s.status === 'completed');
  const totalMinutes = completed.reduce((sum, s) => sum + (s.totalMinutes || 0), 0);
  const uniqueEmps = new Set(filtered.map((s) => s.userId)).size;
  const activeCount = Object.keys(activeShiftsMap).length;

  const empSummary = {};
  completed.forEach((s) => {
    if (!empSummary[s.userId]) {
      empSummary[s.userId] = { name: s.userName, email: s.userEmail, shifts: 0, totalMin: 0, beforeFourMin: 0, afterFourMin: 0 };
    }
    empSummary[s.userId].shifts++;
    const shiftMin = s.totalMinutes || 0;
    empSummary[s.userId].totalMin += shiftMin;
    // Split hours by 4 PM boundary
    const inDate = s.clockIn?.toDate ? s.clockIn.toDate() : new Date(s.clockIn);
    const outDate = s.clockOut?.toDate ? s.clockOut.toDate() : null;
    if (inDate && outDate) {
      const fourPM = new Date(inDate);
      fourPM.setHours(16, 0, 0, 0);
      if (outDate <= fourPM) {
        // Entire shift before 4 PM
        empSummary[s.userId].beforeFourMin += shiftMin;
      } else if (inDate >= fourPM) {
        // Entire shift after 4 PM
        empSummary[s.userId].afterFourMin += shiftMin;
      } else {
        // Shift spans 4 PM — split at boundary
        const beforeMin = Math.round((fourPM - inDate) / 60000);
        const afterMin = Math.round((outDate - fourPM) / 60000);
        empSummary[s.userId].beforeFourMin += beforeMin;
        empSummary[s.userId].afterFourMin += afterMin;
      }
    } else {
      empSummary[s.userId].beforeFourMin += shiftMin;
    }
  });

  // Day-wise grouping for the shift log
  const shiftsByDay = (() => {
    const map = {};
    filtered.forEach(s => {
      const inTime = s.clockIn?.toDate ? s.clockIn.toDate() : new Date(s.clockIn);
      const dayKey = inTime.toLocaleDateString('en-CA');
      if (!map[dayKey]) map[dayKey] = { date: inTime, shifts: [] };
      map[dayKey].shifts.push(s);
    });
    return Object.entries(map)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, val]) => ({
        key,
        date: val.date,
        shifts: val.shifts.sort((a, b) => {
          const aT = a.clockIn?.toDate ? a.clockIn.toDate() : new Date(a.clockIn);
          const bT = b.clockIn?.toDate ? b.clockIn.toDate() : new Date(b.clockIn);
          return aT - bT;
        }),
      }));
  })();

  // ══════════════════════════════════════════════════════════
  // FEATURE: CSV Export
  // ══════════════════════════════════════════════════════════
  const exportCSV = () => {
    const rows = [['Employee', 'Email', 'Shifts', 'Before 4PM (h)', 'After 4PM (h)', 'Total Hours (h)', 'Avg/Shift (h)', 'Hourly Rate ($)', 'Labor Cost ($)', 'Anomalies to review']];
    const vals = Object.entries(empSummary).sort(([a], [b]) => rankOf(a) - rankOf(b)); // team order
    let tShifts=0, tBefore=0, tAfter=0, tTotal=0, tCost=0;
    vals.forEach(([uid, e]) => {
      const rate = laborRates[uid] || 0;
      const totalH = (e.totalMin / 60);
      const cost = totalH * rate;
      tShifts += e.shifts; tBefore += e.beforeFourMin; tAfter += e.afterFourMin; tTotal += e.totalMin; tCost += cost;
      rows.push([e.name, e.email, e.shifts, (e.beforeFourMin/60).toFixed(1), (e.afterFourMin/60).toFixed(1), totalH.toFixed(1), (e.totalMin/e.shifts/60).toFixed(1), rate.toFixed(2), cost.toFixed(2), anomalyCountByUser[uid] || 0]);
    });
    rows.push(['TOTAL', `${vals.length} employees`, tShifts, (tBefore/60).toFixed(1), (tAfter/60).toFixed(1), (tTotal/60).toFixed(1), tShifts?(tTotal/tShifts/60).toFixed(1):'0', '', tCost.toFixed(2)]);
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `shift-hours-${dateRange}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ══════════════════════════════════════════════════════════
  // FEATURE: Labor Rate Save
  // ══════════════════════════════════════════════════════════
  const saveLaborRate = async () => {
    if (!rateModal || rateSaving) return;
    const rate = parseFloat(rateInput);
    if (isNaN(rate) || rate < 0) { alert('Enter a valid hourly rate.'); return; }
    setRateSaving(true);
    try {
      await setDoc(doc(db, 'laborRates', rateModal.userId), { hourlyRate: rate, updatedAt: Timestamp.now() });
      setLaborRates(prev => ({ ...prev, [rateModal.userId]: rate }));
      setRateModal(null); setRateInput('');
    } catch (err) { console.error('Failed to save rate:', err); alert('Failed to save.'); }
    setRateSaving(false);
  };

  // ══════════════════════════════════════════════════════════
  // FEATURE: Shift Swap
  // ══════════════════════════════════════════════════════════
  const submitSwapRequest = async () => {
    if (!swapModal || !swapTargetUser || swapSaving) return;
    setSwapSaving(true);
    try {
      const targetEmp = allStaff.find(s => s.uid === swapTargetUser);
      await addDoc(collection(db, 'shiftSwaps'), {
        plannedShiftId: swapModal.shiftId,
        shiftDate: swapModal.shiftDate,
        shiftStartTime: swapModal.startTime,
        shiftEndTime: swapModal.endTime,
        shiftPosition: swapModal.position,
        fromUserId: swapModal.userId,
        fromUserName: swapModal.userName,
        toUserId: swapTargetUser,
        toUserName: targetEmp?.name || '',
        status: 'pending',
        createdAt: Timestamp.now(),
        createdBy: user.uid,
      });
      showPlanToast('Swap request submitted!');
      setSwapModal(null); setSwapTargetUser('');
      loadSwapRequests();
    } catch (err) { console.error('Swap request failed:', err); alert('Failed to submit swap.'); }
    setSwapSaving(false);
  };

  const handleSwapAction = async (swapId, action) => {
    try {
      const swapDoc = swapRequests.find(s => s.id === swapId);
      if (action === 'approved' && swapDoc) {
        // Reassign the planned shift first, so a failure doesn't leave the swap marked approved
        const target = allStaff.find(s => s.uid === swapDoc.toUserId) || {};
        await updateDoc(doc(db, 'scheduledShifts', swapDoc.plannedShiftId), {
          userId: swapDoc.toUserId, userName: swapDoc.toUserName,
          userEmail: target.email || '', userRole: target.role || '', updatedAt: Timestamp.now(),
        });
      }
      await updateDoc(doc(db, 'shiftSwaps', swapId), { status: action, resolvedAt: Timestamp.now(), resolvedBy: user.uid });
      showPlanToast(action === 'approved' ? 'Swap approved — shift reassigned!' : 'Swap request rejected.');
      loadSwapRequests();
      if (action === 'approved') loadPlanned();
    } catch (err) { console.error('Swap action failed:', err); alert('Failed.'); }
  };

  // Labor cost for summary
  const empLaborCost = (userId, totalMin) => {
    const rate = laborRates[userId] || 0;
    return rate ? (totalMin / 60 * rate) : 0;
  };

  return (
    <div className="shift-page">
      <div className="shift-page__inner shift-dash-wide">

        {/* ── Toast ── */}
        {planToast && (
          <div className="sp-toast" role="status">
            <span>{planToast.msg}</span>
            {planToast.action && (
              <button
                className="sp-toast__action"
                onClick={() => { const { run } = planToast.action; clearTimeout(toastTimerRef.current); setPlanToast(null); run(); }}
              >{planToast.action.label}</button>
            )}
          </div>
        )}

        {/* ══ Hero Banner ══ */}
        <div className="sdh-hero">
          {/* ambient blobs */}
          <div className="sdh-hero__blob sdh-hero__blob--1" />
          <div className="sdh-hero__blob sdh-hero__blob--2" />
          <div className="sdh-hero__blob sdh-hero__blob--3" />

          <div className="sdh-hero__body">
            {/* ── Left: identity ── */}
            <div className="sdh-hero__identity">
              <span className="sdh-hero__eyebrow">⏱ SHIFT MANAGEMENT</span>
              <h1 className="sdh-hero__title">
                {dashView === 'planner' ? 'Shift Planner' : dashView === 'availability' ? 'Availability' : 'Shift Dashboard'}
              </h1>
              <p className="sdh-hero__sub">
                {dashView === 'planner'
                  ? 'Plan & assign your team\u2019s weekly schedule'
                  : dashView === 'availability'
                  ? 'View & manage your team\u2019s weekly availability'
                  : canViewHours ? 'Track attendance, hours & team activity in real-time' : 'Manage employee clock-ins and clock-outs'}
              </p>
            </div>

            {/* ── Right: clock OR week-nav ── */}
            {dashView === 'dashboard' ? (
              <div className="sdh-hero__clock-panel">
                <div className="sdh-hero__clock">
                  {now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                </div>
                <div className="sdh-hero__clock-meta">
                  <div className="sdh-hero__date">
                    {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </div>
                  <div className="sdh-hero__live-pill">
                    <span className="sdh-hero__live-dot" />
                    {activeCount} active now
                  </div>
                </div>
              </div>
            ) : dashView === 'availability' ? (
              <div className="sdh-hero__avail-kpis">
                {PLAN_DAYS_FULL.map((day, i) => {
                  const c = availDayCounts[day] || {};
                  const total = activeStaff.length || 1;
                  const avPct = Math.round(((c.available||0) + (c.partial||0)) / total * 100);
                  return (
                    <div key={day} className={`sdh-avkpi${avPct < 50 ? ' sdh-avkpi--low' : ''}`}>
                      <span className="sdh-avkpi__day">{PLAN_DAYS[i]}</span>
                      <span className="sdh-avkpi__pct">{avPct}%</span>
                      <span className="sdh-avkpi__lbl">avail</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* week being planned — switching weeks happens in the week bar above the grid */
              <div className="sdh-hero__week-nav">
                <div className="sdh-hero__week-info">
                  <span className="sdh-hero__week-range">{weekRangeLabel(weekStart)}</span>
                  <span className={`sdh-hero__week-rel${viewingThisWeek?'':' sdh-hero__week-rel--other'}`}>{weekRelLabel(weekStart, thisWeekStart)}</span>
                </div>
              </div>
            )}
          </div>

          {/* ── KPI strip (dashboard only) ── */}
          {dashView === 'dashboard' && (
            <div className="sdh-hero__kpi-strip">
              <div className="sdh-kpi">
                <span className="sdh-kpi__icon">🟢</span>
                <div className="sdh-kpi__body">
                  <span className="sdh-kpi__val">{activeCount}</span>
                  <span className="sdh-kpi__lbl">Active Now</span>
                </div>
              </div>
              <div className="sdh-kpi">
                <span className="sdh-kpi__icon">📅</span>
                <div className="sdh-kpi__body">
                  <span className="sdh-kpi__val">{todayScheduled.length}</span>
                  <span className="sdh-kpi__lbl">Scheduled Today</span>
                </div>
              </div>
              <div className="sdh-kpi">
                <span className="sdh-kpi__icon">👥</span>
                <div className="sdh-kpi__body">
                  <span className="sdh-kpi__val">{activeStaff.length}</span>
                  <span className="sdh-kpi__lbl">Total Staff</span>
                </div>
              </div>
              <div className="sdh-kpi">
                <span className="sdh-kpi__icon">⏰</span>
                <div className="sdh-kpi__body">
                  <span className="sdh-kpi__val">{(totalMinutes/60).toFixed(1)}h</span>
                  <span className="sdh-kpi__lbl">Hours This Period</span>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab bar ── */}
          <div className="sdh-hero__tabs">
            <button className={`sdh-tab${dashView==='dashboard'?' sdh-tab--active':''}`} onClick={()=>setDashView('dashboard')}>
              <span className="sdh-tab__icon">📊</span> Dashboard
            </button>
            <button className={`sdh-tab${dashView==='planner'?' sdh-tab--active':''}`} onClick={()=>setDashView('planner')}>
              <span className="sdh-tab__icon">📅</span> Shift Planner
            </button>
            <button className={`sdh-tab${dashView==='availability'?' sdh-tab--active':''}`} onClick={()=>setDashView('availability')}>
              <span className="sdh-tab__icon">🗓</span> Availability
            </button>
          </div>
        </div>

        {/* ── Alerts Panel (dashboard) ── */}
        {dashView==='dashboard' && (lateAlerts.length > 0 || swapRequests.length > 0) && (
          <div className="sdh-alerts">
            {/* Late clock-in alerts */}
            {lateAlerts.map(a => (
              <div key={a.userId} className="sdh-alert sdh-alert--late">
                <span className="sdh-alert__icon">🔴</span>
                <div className="sdh-alert__body">
                  <strong>{a.name}</strong> hasn't clocked in — scheduled at {a.scheduledTime} ({a.lateMin}m late)
                </div>
                <span className="sdh-alert__badge">{a.position}</span>
              </div>
            ))}
            {/* Swap requests */}
            {canEdit && swapRequests.map(sw => (
              <div key={sw.id} className="sdh-alert sdh-alert--swap">
                <span className="sdh-alert__icon">🔄</span>
                <div className="sdh-alert__body">
                  <strong>{sw.fromUserName}</strong> wants to swap {pFmt12(sw.shiftStartTime)}–{pFmt12(sw.shiftEndTime)} on {sw.shiftDate} with <strong>{sw.toUserName}</strong>
                </div>
                {canApprove && (
                  <div className="sdh-alert__actions">
                    <button className="sdh-alert__btn sdh-alert__btn--approve" onClick={() => handleSwapAction(sw.id, 'approved')}>Approve</button>
                    <button className="sdh-alert__btn sdh-alert__btn--reject" onClick={() => handleSwapAction(sw.id, 'rejected')}>Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Today's Shifts Strip (dashboard only) ── */}
        {dashView==='dashboard' && (
          <div className="sdh-today" role="region" aria-label="Today's scheduled shifts">
            <div className="sdh-today__hd">
              <div className="sdh-today__hd-left">
                <span className="sdh-today__dot" />
                <span className="sdh-today__title">Today's Scheduled Shifts</span>
                <span className="sdh-today__date">{new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</span>
              </div>
              <div className="sdh-today__hd-right">
                <div className="sdh-today__order" role="group" aria-label="Order today's shifts">
                  {[['team','👥 Team order'],['time','🕐 By time']].map(([v,label]) => (
                    <button key={v} type="button" aria-pressed={todaySort===v}
                      className={`sdh-today__order-btn${todaySort===v?' sdh-today__order-btn--on':''}`}
                      onClick={()=>setTodaySort(v)}>{label}</button>
                  ))}
                </div>
                {canEdit && (
                  <button className="sdh-today__manage" onClick={()=>setDashView('planner')}>
                    ✏️ Manage Planner
                  </button>
                )}
              </div>
            </div>
            {todayScheduled.length===0 ? (
              <div className="sdh-today__empty">
                <span className="sdh-today__empty-icon">🗓</span>
                <span>No shifts scheduled for today.{canEdit && ' Open the Shift Planner to assign shifts.'}</span>
              </div>
            ) : (
              <div className="sdh-today__cards">
                {todayShown.map(s=>{
                  const pos=pPosMeta(s.position);
                  const clocked=todayActualMap[`${s.userId}|${s.date}`];
                  const statusLabel = clocked==='active'||clocked==='on-break' ? 'In' : clocked==='completed' ? 'Done' : 'Scheduled';
                  const statusCls = clocked==='active'||clocked==='on-break' ? 'active' : clocked==='completed' ? 'done' : 'pending';
                  return (
                    <div key={s.id} className="sdh-shift-card" style={{'--pos-color':pos.color,'--pos-bg':pos.bg}}>
                      <div className="sdh-shift-card__bar" />
                      <div className="sdh-shift-card__avatar">{pInitials(s.userName)}</div>
                      <div className="sdh-shift-card__body">
                        <span className="sdh-shift-card__name">{s.userName}</span>
                        <span className="sdh-shift-card__pos">{s.position}</span>
                      </div>
                      <div className="sdh-shift-card__time">
                        <span className="sdh-shift-card__clock-ico">🕐</span>
                        {pFmt12(s.startTime)}–{pFmt12(s.endTime)}
                      </div>
                      <div className="sdh-shift-card__hrs">{pShiftHours(s.startTime,s.endTime).toFixed(1)}h</div>
                      <div className={`sdh-shift-card__badge sdh-shift-card__badge--${statusCls}`}>{statusLabel}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════
             AVAILABILITY TAB
        ══════════════════════════════════════════════════ */}
        {dashView==='availability' && (
          <>
            {/* ── Toolbar ── */}
            <div className="avt-toolbar">
              <div className="avt-toolbar__left">
                <div className="avt-search-wrap">
                  <span className="avt-search-icon">🔍</span>
                  <input
                    className="avt-search"
                    placeholder="Search employee…"
                    value={availSearchQ}
                    onChange={e=>setAvailSearchQ(e.target.value)}
                  />
                  {availSearchQ && <button className="avt-search-clear" onClick={()=>setAvailSearchQ('')}>×</button>}
                </div>
                <div className="avt-role-btns">
                  {[['all','👥 All'],['employee','👷 Employees'],['manager','🎯 Managers']].map(([v,l])=>(
                    <button key={v} className={`avt-role-btn${availFilterRole===v?' avt-role-btn--active':''}`} onClick={()=>setAvailFilterRole(v)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="avt-toolbar__right">
                <div className="avt-view-toggle">
                  <button className={`avt-view-btn${availViewMode==='grid'?' avt-view-btn--active':''}`} onClick={()=>setAvailViewMode('grid')} title="Grid view">⊞</button>
                  <button className={`avt-view-btn${availViewMode==='cards'?' avt-view-btn--active':''}`} onClick={()=>setAvailViewMode('cards')} title="Card view">⊟</button>
                </div>
                {canEdit && (
                  <button className="avt-bulk-btn" onClick={bulkSetAllAvailable} disabled={availBulkSaving}>
                    {availBulkSaving ? '⏳ Setting…' : '✅ Set All Available'}
                  </button>
                )}
              </div>
            </div>

            {/* ── Coverage day strip ── */}
            <div className="avt-coverage-strip">
              <div className="avt-coverage-strip__label">Weekly Coverage</div>
              <div className="avt-coverage-strip__days">
                {PLAN_DAYS_FULL.map((day, i) => {
                  const c = availDayCounts[day] || {};
                  const total = activeStaff.length || 1;
                  const avCount = (c.available||0) + (c.partial||0);
                  const pct = Math.round(avCount / total * 100);
                  const level = pct >= 75 ? 'good' : pct >= 40 ? 'ok' : 'low';
                  return (
                    <div key={day} className={`avt-cov-day avt-cov-day--${level}`}>
                      <span className="avt-cov-day__name">{PLAN_DAYS[i]}</span>
                      <div className="avt-cov-day__bar-wrap">
                        <div className="avt-cov-day__bar-fill" style={{width:`${pct}%`}} />
                      </div>
                      <span className="avt-cov-day__count">{avCount}/{total}</span>
                      <div className="avt-cov-day__pills">
                        {(c.available||0) > 0 && <span className="avt-pill avt-pill--available">{c.available}✅</span>}
                        {(c.partial||0) > 0 && <span className="avt-pill avt-pill--partial">{c.partial}⚡</span>}
                        {(c.unavailable||0) > 0 && <span className="avt-pill avt-pill--unavailable">{c.unavailable}🚫</span>}
                        {(c.unknown||0) > 0 && <span className="avt-pill avt-pill--unknown">{c.unknown}?</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Legend ── */}
            <div className="avt-legend">
              <span className="avt-legend__item avt-legend__item--available">✅ Available</span>
              <span className="avt-legend__item avt-legend__item--partial">⚡ Partial hours</span>
              <span className="avt-legend__item avt-legend__item--unavailable">🚫 Not Available</span>
              <span className="avt-legend__item avt-legend__item--unknown">❓ Not set</span>
              {canEdit && <span className="avt-legend__hint">Click any cell to change</span>}
            </div>

            {/* ── Staff count ── */}
            {visibleAvailStaff.length > 0 && (
              <div className="avt-staff-count">
                Showing <strong>{visibleAvailStaff.length}</strong> of <strong>{activeStaff.length}</strong> employees
              </div>
            )}

            {/* ══ GRID VIEW ══ */}
            {availViewMode === 'grid' && (
              <div className="avt-grid-wrapper">
                <table className="avt-grid">
                  <colgroup>
                    <col style={{width:'160px'}} />
                    {PLAN_DAYS_FULL.map(d => <col key={d} />)}
                  </colgroup>
                  <thead>
                    <tr className="avt-grid__head-row">
                      <th className="avt-grid__th avt-grid__th--emp">Employee</th>
                      {PLAN_DAYS_FULL.map((day, i) => (
                        <th key={day} className="avt-grid__th">
                          <span className="avt-grid__day-name">{PLAN_DAYS[i]}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleAvailStaff.length === 0 ? (
                      <tr><td colSpan={8} className="avt-grid__empty">No employees found.</td></tr>
                    ) : visibleAvailStaff.map(emp => (
                      <tr key={emp.uid} className="avt-grid__row">
                        <td className="avt-grid__emp-cell">
                          <div className="avt-emp">
                            <div className="avt-emp__avatar" style={['manager','admin','owner'].includes(emp.role)
                              ? {background:'linear-gradient(135deg,#06b6d4,#0891b2)'}
                              : {background:'linear-gradient(135deg,#f59e0b,#d97706)'}}>
                              {pInitials(emp.name)}
                            </div>
                            <div className="avt-emp__info">
                              <span className="avt-emp__name">{emp.name||emp.email}</span>
                              <span className="avt-emp__role">{emp.role}</span>
                            </div>
                          </div>
                        </td>
                        {PLAN_DAYS_FULL.map(day => {
                          const d = availMap[emp.uid]?.days?.[day];
                          const type = d?.type || 'unknown';
                          const cellKey = `${emp.uid}|${day}`;
                          const isSaving = availCellSaving === cellKey;
                          const isEditing = availInlineEdit?.userId === emp.uid && availInlineEdit?.dayName === day;
                          return (
                            <td
                              key={day}
                              className={`avt-grid__cell avt-grid__cell--${type}${canEdit?' avt-grid__cell--editable':''}${isSaving?' avt-grid__cell--saving':''}`}
                              onClick={() => canEdit && !isSaving && setAvailInlineEdit(isEditing ? null : {userId: emp.uid, dayName: day})}
                            >
                              {isSaving ? (
                                <span className="avt-cell__saving">…</span>
                              ) : isEditing ? (
                                <div className="avt-inline-pop" onClick={e=>e.stopPropagation()}>
                                  {[
                                    {t:'available', icon:'✅', label:'Available'},
                                    {t:'partial', icon:'⚡', label:'Partial'},
                                    {t:'unavailable', icon:'🚫', label:'Not Available'},
                                  ].map(({t, icon, label}) => (
                                    <button
                                      key={t}
                                      className={`avt-inline-opt avt-inline-opt--${t}${type===t?' avt-inline-opt--active':''}`}
                                      onClick={() => {
                                        if (t === 'partial') {
                                          // Open full modal for partial (need time pickers)
                                          const existing = availMap[emp.uid];
                                          const days = {};
                                          PLAN_DAYS_FULL.forEach(dx => {
                                            days[dx] = existing?.days?.[dx] ? {...existing.days[dx]} : {type:'available'};
                                          });
                                          days[day] = { type: 'partial', startTime: d?.startTime||'09:00', endTime: d?.endTime||'17:00' };
                                          setAvailDays(days);
                                          setAvailModal({userId: emp.uid, userName: emp.name||emp.email});
                                          setAvailInlineEdit(null);
                                        } else {
                                          saveAvailDay(emp.uid, day, t);
                                        }
                                      }}
                                    >
                                      {icon} {label}
                                    </button>
                                  ))}
                                  <button className="avt-inline-cancel" onClick={()=>setAvailInlineEdit(null)}>✕</button>
                                </div>
                              ) : (
                                <div className="avt-cell__content">
                                  <span className="avt-cell__icon">
                                    {type==='available'?'✅':type==='partial'?'⚡':type==='unavailable'?'🚫':'❓'}
                                  </span>
                                  {type==='partial' && d?.startTime && (
                                    <span className="avt-cell__time">{pFmt12(d.startTime)}–{pFmt12(d.endTime)}</span>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ══ CARDS VIEW ══ */}
            {availViewMode === 'cards' && (
              <div className="avt-cards">
                {visibleAvailStaff.length === 0 && (
                  <div className="avt-cards__empty">No employees found.</div>
                )}
                {visibleAvailStaff.map(emp => {
                  const empDays = availMap[emp.uid]?.days;
                  const availCount = empDays ? PLAN_DAYS_FULL.filter(d => empDays[d]?.type === 'available').length : 0;
                  const partialCount = empDays ? PLAN_DAYS_FULL.filter(d => empDays[d]?.type === 'partial').length : 0;
                  const unavailCount = empDays ? PLAN_DAYS_FULL.filter(d => empDays[d]?.type === 'unavailable').length : 0;
                  return (
                    <div key={emp.uid} className="avt-card">
                      <div className="avt-card__header">
                        <div className="avt-card__avatar" style={['manager','admin','owner'].includes(emp.role)
                          ? {background:'linear-gradient(135deg,#06b6d4,#0891b2)'}
                          : {background:'linear-gradient(135deg,#f59e0b,#d97706)'}}>
                          {pInitials(emp.name)}
                        </div>
                        <div className="avt-card__identity">
                          <span className="avt-card__name">{emp.name||emp.email}</span>
                          <span className="avt-card__role">{emp.role}</span>
                        </div>
                        <div className="avt-card__summary">
                          {availCount > 0 && <span className="avt-pill avt-pill--available">{availCount}d avail</span>}
                          {partialCount > 0 && <span className="avt-pill avt-pill--partial">{partialCount}d partial</span>}
                          {unavailCount > 0 && <span className="avt-pill avt-pill--unavailable">{unavailCount}d off</span>}
                          {!empDays && <span className="avt-pill avt-pill--unknown">Not set</span>}
                        </div>
                        {canEdit && (
                          <button className="avt-card__edit-btn" onClick={e=>openAvailModal(e,emp)}>✏️ Edit</button>
                        )}
                      </div>
                      <div className="avt-card__days">
                        {PLAN_DAYS_FULL.map((day, i) => {
                          const d = empDays?.[day];
                          const type = d?.type || 'unknown';
                          return (
                            <div key={day} className={`avt-card__day avt-card__day--${type}`}>
                              <span className="avt-card__day-name">{PLAN_DAYS[i]}</span>
                              <span className="avt-card__day-icon">
                                {type==='available'?'✅':type==='partial'?'⚡':type==='unavailable'?'🚫':'❓'}
                              </span>
                              {type==='partial' && d?.startTime && (
                                <span className="avt-card__day-time">{pFmt12(d.startTime)}–{pFmt12(d.endTime)}</span>
                              )}
                              {type==='available' && <span className="avt-card__day-sub">All day</span>}
                              {type==='unavailable' && <span className="avt-card__day-sub">Off</span>}
                              {type==='unknown' && <span className="avt-card__day-sub">Not set</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Empty state if no staff ── */}
            {activeStaff.length === 0 && (
              <div className="avt-empty-state">
                <div className="avt-empty-state__icon">👥</div>
                <div className="avt-empty-state__title">No employees found</div>
                <div className="avt-empty-state__sub">Add staff members to manage availability.</div>
              </div>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════
             PLANNER VIEW
        ══════════════════════════════════════════════════ */}
        {dashView==='planner' && (
          <>
            {/* ── Week bar: which week you're planning (sits right above the grid, like 7shifts/Deputy) ── */}
            <div className="sp-weekbar">
              <div className="sp-weekbar__nav">
                <button type="button" className="sp-weekbar__arrow" onClick={()=>goToWeek(pAddDays(weekStart,-7))} aria-label="Back one week" title="Back one week">‹</button>
                <label className="sp-weekbar__range" title="Pick any date to jump to its week">
                  <span>📅 {weekRangeLabel(weekStart)}</span>
                  <input
                    type="date" className="sp-weekbar__picker" value={weekStart} aria-label="Jump to the week of a date"
                    onClick={e=>{ try { e.currentTarget.showPicker?.(); } catch { /* older browsers open it on tap */ } }}
                    onChange={e=>{ if(e.target.value) goToWeek(pGetWeekMonday(new Date(e.target.value+'T00:00:00'))); }}
                  />
                </label>
                <button type="button" className="sp-weekbar__arrow" onClick={()=>goToWeek(pAddDays(weekStart,7))} aria-label="Forward one week" title="Forward one week">›</button>
                <span className={`sp-weekbar__rel sp-weekbar__rel--${viewingThisWeek?'now':weekStart>thisWeekStart?'future':'past'}`}>
                  {weekRelLabel(weekStart, thisWeekStart)}
                </span>
                <span className="sp-weekbar__status">
                  {planLoading ? 'Loading…'
                    : planned.some(p=>p.status!=='cancelled')
                      ? `${planned.filter(p=>p.status!=='cancelled').length} shifts · ${Object.values(dayPlanHours).reduce((a,b)=>a+b,0).toFixed(1)}h`
                      : 'Nothing scheduled yet'}
                </span>
                {canEdit && !planLoading && planned.length > 0 && (
                  <button type="button" className="sp-weekbar__clear" onClick={openClearWeek} title={`Delete shifts in ${weekRangeLabel(weekStart)}`}>
                    🗑 Clear week
                  </button>
                )}
              </div>
              <div className="sp-weekbar__jump" role="group" aria-label="Jump to week">
                {[[thisWeekStart,'This week'],[pAddDays(thisWeekStart,7),'Next week']].map(([ws,label]) => (
                  <button
                    key={label} type="button" aria-pressed={weekStart===ws}
                    className={`sp-weekbar__jump-btn${weekStart===ws?' sp-weekbar__jump-btn--on':''}`}
                    onClick={()=>goToWeek(ws)}
                  >{label}</button>
                ))}
              </div>
            </div>

            {/* Overtime Alerts */}
            {overtimeAlerts.length > 0 && (
              <div className="sdh-alerts" style={{marginBottom:12}}>
                {overtimeAlerts.map(a => (
                  <div key={a.userId} className={`sdh-alert ${a.type==='over'?'sdh-alert--late':'sdh-alert--overtime-near'}`}>
                    <span className="sdh-alert__icon">{a.type==='over'?'🚨':'⚠️'}</span>
                    <div className="sdh-alert__body">{a.msg}</div>
                    <span className="sdh-alert__badge">{a.hours.toFixed(1)}h / {OVERTIME_THRESHOLD}h</span>
                  </div>
                ))}
              </div>
            )}
            {/* Toolbar */}
            <div className="sp-toolbar">
              <div className="sp-toolbar__filters">
                <span className="sp-toolbar__label">View:</span>
                {['all','manager','employee'].map(r=>(
                  <button key={r} className={`sp-filter-btn${planFilterRole===r?' sp-filter-btn--active':''}`} onClick={()=>setPlanFilterRole(r)}>
                    {r==='all'?'👥 All':r==='manager'?'🎯 Managers':'👷 Employees'}
                  </button>
                ))}
              </div>
              <div className="sp-toolbar__center">
                {weekConflictCount > 0 && (
                  <div className="sp-conflict-badge" title="Shifts scheduled for unavailable employees">
                    ⚠️ {weekConflictCount} conflict{weekConflictCount > 1 ? 's' : ''}
                  </div>
                )}
                {unscheduledCount > 0 && (
                  <div className="sp-unscheduled-badge" title="Employees with availability set but no shifts this week">
                    🔔 {unscheduledCount} unscheduled
                  </div>
                )}
                {doubleBookedIds.size > 0 && (
                  <div className="sp-conflict-badge" title="Shifts that overlap another shift of the same person — open one and delete the extra">
                    ⛔ {doubleBookedIds.size} double-booked
                  </div>
                )}
              </div>
              <div className="sp-toolbar__right">
                <div className="sp-sort-wrap">
                  <span className="sp-sort-label">Sort:</span>
                  <select className="sp-sort-select" aria-label="Sort staff" value={planSortMode} onChange={e=>setPlanSortMode(e.target.value)}>
                    <option value="team">Team order</option>
                    <option value="name">Name A–Z</option>
                    <option value="hours">Hours ↓</option>
                    <option value="avail">Availability ↓</option>
                  </select>
                </div>
                {canEdit && (
                  <button className="sp-team-btn" onClick={()=>{ setTeamSave('idle'); setTeamOpen(true); }} title="Arrange the team order, deactivate or reactivate people">
                    👥 Team{Object.keys(team.inactive).length ? <span className="sp-team-btn__count">{Object.keys(team.inactive).length} inactive</span> : null}
                  </button>
                )}
                <button
                  className={`sp-copy-btn${copyBusy?' sp-copy-btn--busy':''}`} onClick={copyLastWeek} disabled={copyBusy||!canEdit||planLoading}
                  title={`Copy every shift from ${weekRangeLabel(pAddDays(weekStart,-7))} into this week — days already planned are skipped`}
                >
                  {copyBusy?'⏳ Copying…':`📋 Copy ${weekRangeLabel(pAddDays(weekStart,-7))}`}
                </button>
                <button
                  className={`sp-preview-btn${showPlanPreview?' sp-preview-btn--active':''}`}
                  onClick={()=>setShowPlanPreview(v=>!v)}
                  title="Weekly schedule preview"
                >
                  {showPlanPreview?'✕ Close':'👁 Preview'}
                </button>
              </div>
            </div>

            {/* ── Weekly Preview Panel ── */}
            {showPlanPreview && (() => {
              const assignedStaff = previewStaff;
              return (
                <div className="sp-preview-panel">
                  <div className="sp-preview-panel__header">
                    <div className="sp-preview-panel__title">
                      <span className="sp-preview-panel__icon">📅</span>
                      <span>Weekly Schedule Preview</span>
                      <span className="sp-preview-panel__range">
                        {pFmtDate(weekDays[0])} — {pFmtDate(weekDays[weekDays.length-1])}
                      </span>
                    </div>
                    <div className="sp-preview-panel__meta">
                      <span className="sp-preview-meta-pill">{assignedStaff.length} assigned</span>
                      <span className="sp-preview-meta-pill sp-preview-meta-pill--hours">
                        {Object.values(empPlanHours).reduce((a,b)=>a+b,0).toFixed(1)}h total
                      </span>
                    </div>
                    <div className="sp-preview-panel__share">
                      <div className="sp-share-layout" role="group" aria-label="WhatsApp message layout">
                        <span className="sp-share-layout__lbl">Send as</span>
                        {[
                          ['person', '👤 By person', 'Each person, then the days they work — staff find their own name once'],
                          ['day',    '📅 By day',    'Each day, then each time slot and who works it — good for checking coverage'],
                        ].map(([value, label, tip]) => (
                          <button
                            key={value} type="button" title={tip} aria-pressed={shareLayout===value}
                            className={`sp-share-layout__btn${shareLayout===value?' sp-share-layout__btn--on':''}`}
                            onClick={()=>pickShareLayout(value)}
                          >{label}</button>
                        ))}
                      </div>
                      <button
                        className="sp-share-btn sp-share-btn--wa"
                        onClick={shareWeekWhatsApp}
                        disabled={!assignedStaff.length}
                        title="Open WhatsApp with this week's schedule — pick your staff group or a contact, then send"
                      >💬 Share on WhatsApp</button>
                      <button className="sp-share-btn" onClick={copyWeekText} disabled={!assignedStaff.length} title="Copy the schedule as text">
                        📋 Copy
                      </button>
                    </div>
                  </div>

                  {assignedStaff.length === 0 ? (
                    <div className="sp-preview-empty">
                      <span className="sp-preview-empty__icon">📋</span>
                      <p>No shifts scheduled this week yet.</p>
                      <p className="sp-preview-empty__sub">Click any cell in the grid below to add shifts.</p>
                    </div>
                  ) : (
                    <div className="sp-preview-grid-wrap">
                      <div
                        className="sp-preview-grid"
                        style={{gridTemplateColumns:`200px repeat(${weekDays.length}, 1fr)`}}
                      >
                        {/* Day header row */}
                        <div className="sp-preview-grid__corner" />
                        {weekDays.map((date, i) => (
                          <div
                            key={date}
                            className={`sp-preview-grid__day-hdr${pIsToday(date)?' sp-preview-grid__day-hdr--today':''}`}
                          >
                            <span className="sp-preview-grid__day-name">{PLAN_DAYS[i]}</span>
                            <span className="sp-preview-grid__day-date">{pFmtDate(date)}</span>
                          </div>
                        ))}

                        {/* Employee rows */}
                        {assignedStaff.map(emp => {
                          const isManager = ['manager','admin','owner'].includes(emp.role);
                          return (
                            <React.Fragment key={emp.uid}>
                              {/* Identity cell */}
                              <div className="sp-preview-grid__emp">
                                <div
                                  className="sp-preview-emp-avatar"
                                  style={isManager
                                    ?{background:'linear-gradient(135deg,#06b6d4,#0891b2)'}
                                    :{background:'linear-gradient(135deg,#f59e0b,#d97706)'}}
                                >
                                  {pInitials(emp.name)}
                                </div>
                                <div className="sp-preview-emp-info">
                                  <span className="sp-preview-emp-name">{emp.name||emp.email}</span>
                                  <span className="sp-preview-emp-role">{emp.role}</span>
                                </div>
                                <button
                                  className="sp-preview-wa-btn"
                                  onClick={() => sharePersonWhatsApp(emp)}
                                  aria-label={`Send ${emp.name||emp.email} their shifts on WhatsApp`}
                                  title={waNumber(emp.phone)
                                    ? `Send ${emp.name||emp.email} their shifts on WhatsApp`
                                    : `Send ${emp.name||emp.email} their shifts on WhatsApp — no phone on their profile, so you'll pick the chat`}
                                >💬</button>
                              </div>
                              {/* Day cells */}
                              {weekDays.map((date, i) => {
                                const dayShifts = planMap[`${emp.uid}|${date}`]||[];
                                const sorted = [...dayShifts].sort((a,b)=>a.startTime.localeCompare(b.startTime));
                                const isToday = pIsToday(date);
                                return (
                                  <div
                                    key={date}
                                    className={`sp-preview-grid__cell${isToday?' sp-preview-grid__cell--today':''}${dayShifts.length===0?' sp-preview-grid__cell--empty':''}`}
                                  >
                                    {sorted.length === 0 ? (
                                      <span className="sp-preview-grid__off">—</span>
                                    ) : (
                                      <div className="sp-preview-grid__times">
                                        {sorted.map(s => (
                                          <span key={s.id} className="sp-preview-grid__time">
                                            {pFmt12(s.startTime)}–{pFmt12(s.endTime)}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {assignedStaff.length > 0 && (
                    <p className="sp-preview-panel__tip">💬 next to a name sends that person only their own shifts.</p>
                  )}
                </div>
              );
            })()}

            {/* Grid */}
            {/* A current/future week with nothing in it yet — offer the fastest start */}
            {canEdit && !planLoading && planned.length===0 && weekStart>=thisWeekStart && (
              <div className="sp-empty-week">
                <span className="sp-empty-week__icon">🗓</span>
                <div className="sp-empty-week__body">
                  <strong>{weekRelLabel(weekStart, thisWeekStart)} ({weekRangeLabel(weekStart)}) isn't planned yet</strong>
                  <span>Start from the week before and adjust — or tap any day below to add shifts.</span>
                </div>
                <button className="sp-empty-week__btn" onClick={copyLastWeek} disabled={copyBusy}>
                  {copyBusy?'⏳ Copying…':`📋 Copy ${weekRangeLabel(pAddDays(weekStart,-7))}`}
                </button>
              </div>
            )}

            <div className={`sp-grid-wrapper${planLoading?' sp-grid-wrapper--loading':''}`} aria-busy={planLoading}>
              <table className="sp-grid">
                <colgroup>
                  <col style={{width:'140px'}} />
                  {weekDays.map(d=><col key={d} />)}
                  <col style={{width:'62px'}} />
                </colgroup>
                <thead>
                  <tr className="sp-grid__head-row">
                    <th className="sp-grid__th sp-grid__th--emp">Employee</th>
                    {weekDays.map((date,i)=>(
                      <th key={date} className={`sp-grid__th${pIsToday(date)?' sp-grid__th--today':''}`}>
                        <span className="sp-grid__day-name">{PLAN_DAYS[i]}</span>
                        <span className="sp-grid__day-date">{pFmtDate(date)}</span>
                        {dayPlanHours[date]>0&&<span className="sp-grid__day-hours">{dayPlanHours[date].toFixed(1)}h</span>}
                        {dayPositionMap[date] && (
                          <div className="sp-day-pos-row">
                            {Object.entries(dayPositionMap[date]).map(([pos, cnt]) => {
                              const meta = pPosMeta(pos);
                              return (
                                <span key={pos} className="sp-day-pos-dot" style={{background:meta.color}} title={`${cnt} ${pos}`}>
                                  {cnt}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </th>
                    ))}
                    <th className="sp-grid__th sp-grid__th--total">Wk Hrs</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePlanStaff.length===0 ? (
                    <tr><td colSpan={9} className="sp-grid__empty">No staff found.</td></tr>
                  ) : sortedVisiblePlanStaff.map(emp=>{
                    const inactiveRow = isInactive(emp.uid); // only shown while they still have shifts this week
                    const canAdd = canEdit && !inactiveRow;
                    return (
                    <tr key={emp.uid} className={`sp-grid__row${inactiveRow?' sp-grid__row--inactive':!(empPlanHours[emp.uid]>0)&&availMap[emp.uid]?.days?' sp-grid__row--unscheduled':''}`}>
                      <td className="sp-grid__emp-cell">
                        <div className="sp-emp">
                          <div className="sp-emp__avatar" style={['manager','admin','owner'].includes(emp.role)
                            ?{background:'linear-gradient(135deg,#06b6d4,#0891b2)'}
                            :{background:'linear-gradient(135deg,#f59e0b,#d97706)'}}>
                            {pInitials(emp.name)}
                          </div>
                          <div className="sp-emp__info">
                            <span className="sp-emp__name">{emp.name||emp.email}</span>
                            <span className="sp-emp__role">{emp.role}</span>
                            {inactiveRow && <span className="sp-emp__inactive" title="Deactivated in 👥 Team — remove or reassign these shifts">Inactive</span>}
                            {!inactiveRow && empAvailScore[emp.uid] !== null && empAvailScore[emp.uid] !== undefined && (
                              <span className={`sp-avail-score sp-avail-score--${empAvailScore[emp.uid]>=6?'good':empAvailScore[emp.uid]>=4?'ok':'low'}`}>
                                {empAvailScore[emp.uid]}/7d avail
                              </span>
                            )}
                          </div>
                        </div>
                        {canAdd && (
                          <button className="sp-avail-btn" title="Set availability" onClick={e=>openAvailModal(e,emp)}>
                            📅
                          </button>
                        )}
                      </td>
                      {weekDays.map(date=>{
                        const key=`${emp.uid}|${date}`;
                        const dayShifts=planMap[key]||[];
                        const isClocked=actualMap[key];
                        const cellAvail = getAvailStatus(availMap, emp.uid, date, null, null);
                        return (
                          <td key={date}
                            className={`sp-grid__cell${pIsToday(date)?' sp-grid__cell--today':''}${canAdd?' sp-grid__cell--editable':''}${cellAvail==='unavailable'?' sp-grid__cell--unavail':cellAvail==='partial'?' sp-grid__cell--partial':''}`}
                            onClick={()=>dayShifts.length===0&&openPlanAdd(emp.uid,emp.name||emp.email,date)}
                          >
                            {cellAvail==='unavailable'&&dayShifts.length===0&&(
                              <div className="sp-cell__unavail-hint">🚫 Not available</div>
                            )}
                            {cellAvail==='partial'&&dayShifts.length===0&&(
                              <div className="sp-cell__partial-hint">⚡ Limited</div>
                            )}
                            {dayShifts.map(s=>{
                              const pos=pPosMeta(s.position);
                              const doubled=doubleBookedIds.has(s.id);
                              return (
                                <div key={s.id}
                                  className={`sp-block${s.status==='cancelled'?' sp-block--cancelled':''}${doubled?' sp-block--clash':''}`}
                                  style={{background:pos.bg,borderLeft:`3px solid ${pos.color}`}}
                                  onClick={e=>openPlanEdit(e,s)}
                                >
                                  <div className="sp-block__top">
                                    <span className="sp-block__time">{pFmt12(s.startTime)}–{pFmt12(s.endTime)}</span>
                                    {canEdit&&<button className="sp-block__del" onClick={e=>deletePlanShift(e,s)} title="Remove shift" aria-label="Remove shift">×</button>}
                                  </div>
                                  <div className="sp-block__hours">{pShiftHours(s.startTime,s.endTime).toFixed(1)}h</div>
                                  {doubled&&<div className="sp-block__clash">⛔ Double-booked</div>}
                                  {isClocked&&<div className={`sp-block__status sp-block__status--${isClocked}`}>{isClocked==='active'||isClocked==='on-break'?'🟢 In':'✓ Done'}</div>}
                                  {canEdit && !isClocked && <button className="sp-block__swap" onClick={e=>{e.stopPropagation();setSwapModal({shiftId:s.id,shiftDate:s.date,startTime:s.startTime,endTime:s.endTime,position:s.position,userId:s.userId,userName:s.userName});setSwapTargetUser('');}}>🔄</button>}
                                </div>
                              );
                            })}
                            {dayShifts.length===0&&canAdd&&<div className="sp-cell__add-hint">+ Add</div>}
                            {dayShifts.length>0&&canAdd&&<div className="sp-cell__add-more" onClick={e=>{e.stopPropagation();openPlanAdd(emp.uid,emp.name||emp.email,date);}}>+ Add</div>}
                          </td>
                        );
                      })}
                      <td className="sp-grid__total-cell">
                        <span className={`sp-total${(empPlanHours[emp.uid]||0)>=40?' sp-total--over':''}`}>{(empPlanHours[emp.uid]||0).toFixed(1)}h</span>
                      </td>
                    </tr>
                  );})}
                </tbody>
                <tfoot>
                  <tr className="sp-grid__foot-row">
                    <td className="sp-grid__foot-label">Daily Total</td>
                    {weekDays.map(date=>(
                      <td key={date} className="sp-grid__foot-cell">
                        {(dayPlanHours[date]||0)>0
                          ?<span className="sp-foot-val">{(dayPlanHours[date]||0).toFixed(1)}h</span>
                          :<span className="sp-foot-empty">—</span>}
                      </td>
                    ))}
                    <td className="sp-grid__foot-cell">
                      <span className="sp-foot-val sp-foot-val--total">{Object.values(dayPlanHours).reduce((a,b)=>a+b,0).toFixed(1)}h</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {/* Stats */}
            <div className="sp-stats">
              <div className="sp-stat"><span className="sp-stat__val">{planned.filter(p=>p.status!=='cancelled').length}</span><span className="sp-stat__lbl">Shifts Scheduled</span></div>
              <div className="sp-stat"><span className="sp-stat__val">{visiblePlanStaff.filter(e=>(empPlanHours[e.uid]||0)>0).length}</span><span className="sp-stat__lbl">Staff Assigned</span></div>
              <div className="sp-stat"><span className="sp-stat__val">{Object.values(dayPlanHours).reduce((a,b)=>a+b,0).toFixed(0)}h</span><span className="sp-stat__lbl">Total Hours</span></div>
              <div className="sp-stat"><span className="sp-stat__val">{visiblePlanStaff.filter(e=>(empPlanHours[e.uid]||0)>=40).length}</span><span className="sp-stat__lbl">Full-Time (40h+)</span></div>
              {unscheduledCount > 0 && <div className="sp-stat sp-stat--alert"><span className="sp-stat__val">{unscheduledCount}</span><span className="sp-stat__lbl">⚠ Unscheduled</span></div>}
              {weekConflictCount > 0 && <div className="sp-stat sp-stat--alert"><span className="sp-stat__val">{weekConflictCount}</span><span className="sp-stat__lbl">🚫 Conflicts</span></div>}
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════
             DASHBOARD VIEW
        ══════════════════════════════════════════════════ */}
        {/* ── Employee Roster — always visible ── */}
        {dashView==='dashboard' && rosterStaff.length > 0 && (
          <div className="shift-dash-section">
            <div className="shift-dash-section__header">
              <h3 className="shift-dash-section__title">Employee Roster</h3>
              <span className="shift-dash-section__count">{activeStaff.length} staff</span>
            </div>
            <div className="shift-dash-roster">
              {rosterStaff.map((s) => {
                const active = activeShiftsMap[s.uid];
                const isOnBreak = active?.status === 'on-break';
                const clockInTime = active?.clockIn?.toDate ? active.clockIn.toDate() : null;
                return (
                  <div key={s.uid} className={`shift-dash-emp ${active ? 'shift-dash-emp--active' : ''}`}>
                    <div className="shift-dash-emp__avatar">
                      {(s.name || '?')[0].toUpperCase()}
                    </div>
                    <div className="shift-dash-emp__info">
                      <div className="shift-dash-emp__name">{s.name || '(no name)'}</div>
                      <div className="shift-dash-emp__role">{s.role}</div>
                      {active && (
                        <div className="shift-dash-emp__status">
                          <span className={`shift-dash-emp__dot ${isOnBreak ? 'shift-dash-emp__dot--break' : ''}`} />
                          {isOnBreak ? 'On Break' : 'Working'} since {clockInTime ? fmtTime(clockInTime) : '—'}
                        </div>
                      )}
                      {active && clockInTime && (() => {
                        const totalMs = now - clockInTime;
                        const breakMs = (active.breaks||[]).reduce((acc, b) => {
                          const bs = b.start?.toDate ? b.start.toDate() : new Date(b.start);
                          const be = b.end ? (b.end?.toDate ? b.end.toDate() : new Date(b.end)) : now;
                          return acc + (be - bs);
                        }, 0);
                        const workMs = Math.max(0, totalMs - breakMs);
                        const h = Math.floor(workMs / 3600000);
                        const m = Math.floor((workMs % 3600000) / 60000);
                        return (
                          <div className="shift-dash-emp__duration">
                            ⏱ {h}h {m}m{isOnBreak ? ' worked' : ''}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="shift-dash-emp__action">
                      {active ? (
                        <button
                          className="shift-dash-emp__btn shift-dash-emp__btn--out"
                          onClick={() => handleManagerClockOut(s)}
                          disabled={busy === s.uid}
                        >
                          {busy === s.uid ? '…' : '⏹ Clock Out'}
                        </button>
                      ) : (
                        <button
                          className="shift-dash-emp__btn shift-dash-emp__btn--in"
                          onClick={() => handleManagerClockIn(s)}
                          disabled={busy === s.uid}
                        >
                          {busy === s.uid ? '▶ Clock In' : '▶ Clock In'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══════ OWNER / ADMIN ONLY — Hours & Reports ══════ */}
        {canViewHours && (
          <>
            {/* Divider */}
            <div className="shift-dash-divider">
              <span className="shift-dash-divider__line" />
              <span className="shift-dash-divider__text">📊 Hours & Reports</span>
              <span className="shift-dash-divider__line" />
            </div>

            {/* ── Hour Change Requests (Admin / Owner only) ── */}
            {canApprove && pendingRequests.length > 0 && (
              <div className="shift-dash-section shift-dash-section--requests">
                <div className="shift-dash-section__header">
                  <h3 className="shift-dash-section__title">🔔 Pending Approvals</h3>
                  <span className="shift-dash-section__count shift-dash-section__count--alert">
                    {pendingRequests.length} pending
                  </span>
                </div>
                <div className="shift-req-list">
                  {pendingRequests.map((req) => {
                    const type = req.type || 'edit';
                    const isNewShift = type === 'new-shift';
                    const isDelete = type === 'delete-shift';
                    const newIn  = toDate(req.requestedClockIn);
                    const newOut = toDate(req.requestedClockOut);
                    const newTotal = newIn && newOut ? Math.round((newOut - newIn) / 60000) : 0;
                    const submittedAt = toDate(req.updatedAt) || toDate(req.createdAt);
                    const isManagerReq = req.submittedByRole === 'manager';
                    const origIn  = toDate(req.originalClockIn);
                    const origOut = toDate(req.originalClockOut);
                    const origTotal = origIn && origOut ? Math.round((origOut - origIn) / 60000) : null;
                    const isDuplicate = pendingDupes.has(req.id);
                    const overlaps = isDelete ? [] : (reqOverlaps[req.id] || []);
                    return (
                      <div key={req.id} className={`shift-req-card ${isManagerReq ? 'shift-req-card--manager' : ''}${isDuplicate ? ' shift-req-card--duplicate' : ''}`}>
                        {/* Header */}
                        <div className="shift-req-card__header">
                          <div className="shift-req-card__who">
                            <span className="shift-req-card__avatar">{(req.userName || '?')[0].toUpperCase()}</span>
                            <div>
                              <div className="shift-req-card__name">{req.userName}</div>
                              <div className="shift-req-card__email">{req.userEmail}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                            <span className={`shift-req-card__source-badge shift-req-card__source-badge--${isManagerReq ? 'manager' : 'employee'}`}>
                              {isDelete
                                ? `🗑 ${req.submittedByName || 'Manager'}: delete record`
                                : isNewShift
                                  ? `➕ ${req.submittedByName || req.userName || 'Staff'}: add shift`
                                  : isManagerReq
                                    ? `✏️ ${req.submittedByName || 'Manager'} edited`
                                    : '👤 Employee request'}
                            </span>
                            {submittedAt && (
                              <div className="shift-req-card__submitted">
                                {req.resubmits ? `Updated ×${req.resubmits} · ` : ''}
                                {submittedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {fmtTime(submittedAt)}
                              </div>
                            )}
                          </div>
                        </div>

                        {isDuplicate && (
                          <div className="shift-req-card__dup" role="note">
                            ⧉ Duplicate — the same request was sent again (the newer one is kept). Dismiss this copy.
                          </div>
                        )}

                        {/* Times — add-shift: one block · delete: the record · edit: before → after */}
                        {isDelete ? (
                          <div className="shift-req-card__times shift-req-card__times--single">
                            <div className="shift-req-card__time-col shift-req-card__time-col--old">
                              <span className="shift-req-card__time-lbl">Record to delete</span>
                              <span className="shift-req-card__time-val">{origIn ? fmtTime(origIn) : '—'} — {origOut ? fmtTime(origOut) : 'still open'}</span>
                              {origIn && <span className="shift-req-card__date">{origIn.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' })}</span>}
                              {origTotal != null && <span className="shift-req-card__hours">{Math.floor(origTotal / 60)}h {origTotal % 60}m</span>}
                            </div>
                          </div>
                        ) : isNewShift ? (
                          <div className="shift-req-card__times shift-req-card__times--single">
                            <div className="shift-req-card__time-col shift-req-card__time-col--new">
                              <span className="shift-req-card__time-lbl">New Shift</span>
                              <span className="shift-req-card__time-val">{newIn ? fmtTime(newIn) : '—'} — {newOut ? fmtTime(newOut) : '—'}</span>
                              {newIn && <span className="shift-req-card__date">{newIn.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' })}</span>}
                              <span className="shift-req-card__hours">{Math.floor(newTotal / 60)}h {newTotal % 60}m total</span>
                            </div>
                          </div>
                        ) : (
                          <div className="shift-req-card__times">
                            <div className="shift-req-card__time-col shift-req-card__time-col--old">
                              <span className="shift-req-card__time-lbl">Current</span>
                              <span className="shift-req-card__time-val">
                                {origIn ? fmtTime(origIn) : '—'} — {origOut ? fmtTime(origOut) : '—'}
                              </span>
                              {origIn && <span className="shift-req-card__date">{origIn.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
                            </div>
                            <span className="shift-req-card__arrow">→</span>
                            <div className="shift-req-card__time-col shift-req-card__time-col--new">
                              <span className="shift-req-card__time-lbl">Requested</span>
                              <span className="shift-req-card__time-val">{newIn ? fmtTime(newIn) : '—'} — {newOut ? fmtTime(newOut) : '—'}</span>
                              <span className="shift-req-card__hours">{Math.floor(newTotal / 60)}h {newTotal % 60}m total</span>
                            </div>
                          </div>
                        )}

                        {overlaps.length > 0 && !isDuplicate && (
                          <div className="shift-req-card__overlaps">
                            <strong>⚠ {req.userName} already has {overlaps.length} record{overlaps.length > 1 ? 's' : ''} in this time</strong>
                            <ul>
                              {overlaps.map(o => {
                                const a = toDate(o.clockIn), b = toDate(o.clockOut);
                                return <li key={o.id}>{fmtTime(a)} — {b ? fmtTime(b) : 'still clocked in'}{b ? ` (${Math.round((b - a) / 60000)}m)` : ''}</li>;
                              })}
                            </ul>
                            <span>Approving replaces {overlaps.length > 1 ? 'them' : 'it'} with this shift, so the hours aren't counted twice.</span>
                          </div>
                        )}

                        {req.reason && (
                          <div className="shift-req-card__reason">
                            <span className="shift-req-card__reason-lbl">Reason:</span> {req.reason}
                          </div>
                        )}
                        <div className="shift-req-card__actions">
                          {isDuplicate ? (
                            <button className="shift-req-card__btn shift-req-card__btn--reject" onClick={() => dismissDuplicate(req)} disabled={reqBusy === req.id}>
                              {reqBusy === req.id ? '…' : '⧉ Dismiss duplicate'}
                            </button>
                          ) : (
                            <>
                              <button className="shift-req-card__btn shift-req-card__btn--reject" onClick={() => handleRejectRequest(req)} disabled={reqBusy === req.id}>
                                {reqBusy === req.id ? '…' : '✕ Reject'}
                              </button>
                              <button className="shift-req-card__btn shift-req-card__btn--approve" onClick={() => handleApproveRequest(req)} disabled={reqBusy === req.id}>
                                {reqBusy === req.id ? '…'
                                  : isDelete ? '✓ Approve & Delete'
                                  : overlaps.length ? `✓ Approve & Replace ${overlaps.length}`
                                  : isNewShift ? '✓ Approve & Create' : '✓ Approve & Apply'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Manager's own pending submissions */}
            {role === 'manager' && Object.keys(myPendingEditsMap).length > 0 && (
              <div className="shift-dash-section shift-dash-section--my-edits">
                <div className="shift-dash-section__header">
                  <h3 className="shift-dash-section__title">⏳ My Pending Edit Requests</h3>
                  <span className="shift-dash-section__count">{Object.keys(myPendingEditsMap).length} awaiting admin</span>
                </div>
                <div className="shift-req-list">
                  {Object.values(myPendingEditsMap).map((req) => {
                    const isDelete = req.type === 'delete-shift';
                    const origIn  = toDate(req.originalClockIn) || toDate(req.requestedClockIn);
                    const origOut = toDate(req.originalClockOut);
                    const newIn   = toDate(req.requestedClockIn);
                    const newOut  = toDate(req.requestedClockOut);
                    const newTotal = newIn && newOut ? Math.round((newOut - newIn) / 60000) : 0;
                    return (
                      <div key={req.id} className="shift-req-card shift-req-card--my-pending">
                        <div className="shift-req-card__header">
                          <div className="shift-req-card__who">
                            <span className="shift-req-card__avatar">{(req.userName || '?')[0].toUpperCase()}</span>
                            <div>
                              <div className="shift-req-card__name">{req.userName}</div>
                              <div className="shift-req-card__email">{req.userEmail}</div>
                            </div>
                          </div>
                          <span className="shift-req-card__status-pill">{isDelete ? '🗑 Delete · ' : ''}⏳ Awaiting Admin</span>
                        </div>
                        <div className="shift-req-card__times">
                          <div className="shift-req-card__time-col shift-req-card__time-col--old">
                            <span className="shift-req-card__time-lbl">Current</span>
                            <span className="shift-req-card__time-val">
                              {origIn ? fmtTime(origIn) : '—'} — {origOut ? fmtTime(origOut) : '—'}
                            </span>
                            {origIn && <span className="shift-req-card__date">{origIn.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
                          </div>
                          {!isDelete && newIn && newOut && (
                            <>
                              <span className="shift-req-card__arrow">→</span>
                              <div className="shift-req-card__time-col shift-req-card__time-col--new">
                                <span className="shift-req-card__time-lbl">Requested</span>
                                <span className="shift-req-card__time-val">
                                  {fmtTime(newIn)} — {fmtTime(newOut)}
                                </span>
                                <span className="shift-req-card__hours">{Math.floor(newTotal / 60)}h {newTotal % 60}m total</span>
                              </div>
                            </>
                          )}
                        </div>
                        {req.reason && (
                          <div className="shift-req-card__reason">
                            <span className="shift-req-card__reason-lbl">Reason:</span> {req.reason}
                          </div>
                        )}
                        <div className="shift-req-card__actions">
                          <button
                            className="shift-req-card__btn shift-req-card__btn--reject"
                            onClick={() => handleCancelManagerEdit(req)}
                            disabled={reqBusy === req.id}
                            style={{ flex: 'none', maxWidth: 180 }}
                          >
                            {reqBusy === req.id ? '…' : '✕ Withdraw Request'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="shift-dash-filters">
              <div className="shift-dash-filters__row">
                <div className="shift-dash-filters__group">
                  <label className="shift-dash-filters__label">Period</label>
                  <div className="shift-dash-filters__btns">
                    {[
                      { key: 'today', label: 'Today' },
                      { key: 'week', label: 'This Week' },
                      { key: 'month', label: 'This Month' },
                      { key: 'custom', label: 'Custom' },
                    ].map((p) => (
                      <button
                        key={p.key}
                        className={`shift-dash-filters__btn ${dateRange === p.key ? 'shift-dash-filters__btn--active' : ''}`}
                        onClick={() => setDateRange(p.key)}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                {dateRange === 'custom' && (
                  <div className="shift-dash-filters__custom">
                    <input type="date" className="shift-dash-filters__date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                    <span style={{ color: 'rgba(255,255,255,0.3)' }}>to</span>
                    <input type="date" className="shift-dash-filters__date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                  </div>
                )}
              </div>
              <div className="shift-dash-filters__row">
                <div className="shift-dash-filters__group">
                  <label className="shift-dash-filters__label">Employee</label>
                  <select className="shift-dash-filters__select" value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)}>
                    <option value="all">All Employees</option>
                    {[...employees].sort((a, b) => rankOf(a.id) - rankOf(b.id)).map((emp) => (
                      <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>
                    ))}
                  </select>
                </div>
                <div className="shift-dash-filters__group">
                  <label className="shift-dash-filters__label">Status</label>
                  <select className="shift-dash-filters__select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="on-break">On Break</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="shift-dash-summary">
              <div className="shift-dash-card shift-dash-card--active">
                <span className="shift-dash-card__icon">🟢</span>
                <span className="shift-dash-card__val">{activeNow.length}</span>
                <span className="shift-dash-card__lbl">Clocked In</span>
              </div>
              <div className="shift-dash-card">
                <span className="shift-dash-card__icon">👥</span>
                <span className="shift-dash-card__val">{uniqueEmps}</span>
                <span className="shift-dash-card__lbl">Employees</span>
              </div>
              <div className="shift-dash-card">
                <span className="shift-dash-card__icon">📋</span>
                <span className="shift-dash-card__val">{completed.length}</span>
                <span className="shift-dash-card__lbl">Shifts Done</span>
              </div>
              <div className="shift-dash-card">
                <span className="shift-dash-card__icon">⏰</span>
                <span className="shift-dash-card__val">{Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m</span>
                <span className="shift-dash-card__lbl">Total Hours</span>
              </div>
            </div>

            {/* ── Anomalies: what doesn't match the assigned shifts ── */}
            <div className="shift-dash-section sdh-anom" role="region" aria-label="Anomalies">
              <div className="shift-dash-section__header">
                <h3 className="shift-dash-section__title">⚠️ Anomalies</h3>
                <span className={`shift-dash-section__count${openAnomalies.length ? ' shift-dash-section__count--alert' : ''}`}>
                  {openAnomalies.length ? `${openAnomalies.length} to review` : 'All clear'}
                </span>
              </div>
              <p className="sdh-anom__hint">
                Clock-ins compared with the assigned shifts — within ±1 hour of the scheduled times (and total hours) is fine.
                {anomalies.unplannedDays.length > 0 && (() => {
                  const days = anomalies.unplannedDays;
                  const label = d => new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  const which = days.length <= 4 ? ` (${days.map(label).join(', ')})` : '';
                  return ` ${days.length} day${days.length > 1 ? 's' : ''} with clock-ins had no schedule at all${which}, so ${days.length > 1 ? "they weren't" : "it wasn't"} compared.`;
                })()}
              </p>
              {(openAnomalies.length > 0 || reviewedAnomalies.length > 0) && (
                <div className="sdh-anom__chips" role="group" aria-label="Filter anomalies">
                  {[{ id: 'all', label: 'All' }, ...ANOMALY_GROUPS].map(g => {
                    const n = (showReviewed ? reviewedAnomalies : openAnomalies).filter(e => anomalyMatches(e, g.id)).length;
                    if (g.id !== 'all' && !n) return null;
                    return (
                      <button key={g.id} type="button" aria-pressed={anomalyFilter === g.id}
                        className={`sdh-anom__chip${anomalyFilter === g.id ? ' sdh-anom__chip--on' : ''}`}
                        onClick={() => setAnomalyFilter(g.id)}>{g.label} · {n}</button>
                    );
                  })}
                  {reviewedAnomalies.length > 0 && (
                    <button type="button" className="sdh-anom__chip sdh-anom__chip--reviewed" aria-pressed={showReviewed}
                      onClick={() => { setShowReviewed(v => !v); setAnomalyFilter('all'); }}>
                      {showReviewed ? '← To review' : `✓ Marked OK · ${reviewedAnomalies.length}`}
                    </button>
                  )}
                </div>
              )}
              {anomalyList.length === 0 ? (
                <div className="sdh-anom__empty">{showReviewed ? 'Nothing marked OK in this period.' : '✅ Everything matches the schedule in this period.'}</div>
              ) : (
                <div className="sdh-anom__list">
                  {anomalyList.map(e => (
                    <div key={e.key} className={`sdh-anom__row sdh-anom__row--${topSeverity(e)}`} role="group" aria-label={`${e.userName} — ${e.issues.map(i => i.label).join(', ')}`}>
                      <div className="sdh-anom__who">
                        <strong>{e.userName}</strong>
                        <span>{e.weekly ? `Week of ${e.dayLabel}` : e.dayLabel}</span>
                      </div>
                      <div className="sdh-anom__body">
                        <div className="sdh-anom__tags">
                          {e.issues.map(i => <span key={i.kind} className={`sdh-anom__tag sdh-anom__tag--${i.severity}`}>{i.label}</span>)}
                        </div>
                        <div className="sdh-anom__detail">{e.issues.map(i => i.detail).join(' · ')}</div>
                        {(e.schedText || e.actualText) && (
                          <div className="sdh-anom__times">
                            {e.schedText && <span>Assigned <b>{e.schedText}</b></span>}
                            {e.actualText && <span>Clocked <b>{e.actualText}</b></span>}
                          </div>
                        )}
                        {e.reviewed && (() => {
                          const rv = e.records[0]?.anomalyReview || e.scheduledDocs[0]?.anomalyReview;
                          const at = toDate(rv?.at);
                          return <div className="sdh-anom__reviewed">✓ Marked OK{rv?.byName ? ` by ${rv.byName}` : ''}{at ? ` · ${at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</div>;
                        })()}
                      </div>
                      <div className="sdh-anom__actions">
                        {e.records.slice(0, 2).map(r => (
                          <button key={r.id} type="button" className="sdh-anom__btn" onClick={() => openEditModal(r)} title="Open this clock record">
                            ✏️ {fmtTime(toDate(r.clockIn))}
                          </button>
                        ))}
                        {!e.records.length && e.scheduledDocs.length > 0 && (
                          <button type="button" className="sdh-anom__btn" onClick={() => { setDashView('planner'); goToWeek(pGetWeekMonday(new Date(`${e.day}T00:00:00`))); }}>
                            📅 Planner
                          </button>
                        )}
                        {canEdit && !e.weekly && (
                          <button type="button" className={`sdh-anom__btn${e.reviewed ? '' : ' sdh-anom__btn--ok'}`} onClick={() => markAnomalyOk(e, !e.reviewed)} disabled={ackBusy === e.key}>
                            {ackBusy === e.key ? '…' : e.reviewed ? '↩ Unmark' : '✓ Mark OK'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Currently Active */}
            {activeNow.length > 0 && (
              <div className="shift-dash-section">
                <div className="shift-dash-section__header">
                  <h3 className="shift-dash-section__title">Currently Active</h3>
                  <span className="shift-dash-section__count shift-dash-section__count--live">{activeNow.length} live</span>
                </div>
                <div className="shift-dash-table">
                  <div className="shift-dash-table__head shift-dash-table__head--active">
                    <span>Employee</span><span>Clocked In</span><span>Status</span><span>Breaks</span>
                  </div>
                  {activeNow.map((s) => {
                    const inTime = s.clockIn?.toDate ? s.clockIn.toDate() : new Date(s.clockIn);
                    return (
                      <div key={s.id} className="shift-dash-table__row shift-dash-table__row--active">
                        <div className="shift-dash-table__emp">
                          <div className="shift-dash-table__name">{s.userName}</div>
                          <div className="shift-dash-table__email">{s.userEmail}</div>
                        </div>
                        <span>{fmtTime(inTime)}</span>
                        <span className={`shift-dash-badge ${s.status === 'on-break' ? 'shift-dash-badge--break' : 'shift-dash-badge--active'}`}>
                          {s.status === 'on-break' ? '☕ Break' : '🟢 Working'}
                        </span>
                        <span>{s.breaks?.length || 0}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Employee Hours Summary */}
            {Object.keys(empSummary).length > 0 && (
              <div className="shift-dash-section">
                <div className="shift-dash-section__header">
                  <h3 className="shift-dash-section__title">Employee Hours Summary</h3>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <button className="shift-dash-add-btn" onClick={exportCSV}>📥 Export CSV</button>
                  </div>
                </div>
                <div className="shift-dash-table">
                  <div className="shift-dash-table__head shift-dash-table__head--hours-split">
                    <span>Employee</span><span>Shifts</span><span>Before 4 PM</span><span>After 4 PM</span><span>Total Hours</span><span>Rate</span><span>Cost</span>
                  </div>
                  {Object.entries(empSummary).sort(([a], [b]) => rankOf(a) - rankOf(b)).map(([uid, e]) => {
                    const rate = laborRates[uid] || 0;
                    const cost = empLaborCost(uid, e.totalMin);
                    return (
                      <div key={uid} className="shift-dash-table__row shift-dash-table__row--hours-split">
                        <div className="shift-dash-table__emp">
                          <div className="shift-dash-table__name">
                            {e.name}
                            {anomalyCountByUser[uid] > 0 && (
                              <span className="sdh-anom__count" title="Anomalies to review in this period">⚠ {anomalyCountByUser[uid]}</span>
                            )}
                          </div>
                          <div className="shift-dash-table__email">{e.email}</div>
                        </div>
                        <span>{e.shifts}</span>
                        <span className="shift-dash-table__hours-before">{Math.floor(e.beforeFourMin / 60)}h {e.beforeFourMin % 60}m</span>
                        <span className="shift-dash-table__hours-after">{Math.floor(e.afterFourMin / 60)}h {e.afterFourMin % 60}m</span>
                        <span className="shift-dash-table__hours-val">{Math.floor(e.totalMin / 60)}h {e.totalMin % 60}m</span>
                        <span>
                          {rate > 0 ? (
                            <span className="shift-dash-table__rate" onClick={() => { setRateModal({userId:uid,userName:e.name}); setRateInput(String(rate)); }}>${rate}/h</span>
                          ) : (
                            <button className="shift-dash-table__set-rate" onClick={() => { setRateModal({userId:uid,userName:e.name}); setRateInput(''); }}>Set</button>
                          )}
                        </span>
                        <span className="shift-dash-table__cost">{cost > 0 ? `$${cost.toFixed(0)}` : '—'}</span>
                      </div>
                    );
                  })}
                  {/* ── Totals row ── */}
                  {(() => {
                    const entries = Object.entries(empSummary);
                    const tShifts = entries.reduce((s,[,e]) => s + e.shifts, 0);
                    const tBefore = entries.reduce((s,[,e]) => s + e.beforeFourMin, 0);
                    const tAfter  = entries.reduce((s,[,e]) => s + e.afterFourMin, 0);
                    const tTotal  = entries.reduce((s,[,e]) => s + e.totalMin, 0);
                    const tCost   = entries.reduce((s,[uid,e]) => s + empLaborCost(uid, e.totalMin), 0);
                    return (
                      <div className="shift-dash-table__row shift-dash-table__row--hours-split shift-dash-table__row--totals">
                        <div className="shift-dash-table__emp">
                          <div className="shift-dash-table__name">Total</div>
                          <div className="shift-dash-table__email">{entries.length} employees</div>
                        </div>
                        <span>{tShifts}</span>
                        <span className="shift-dash-table__hours-before">{Math.floor(tBefore / 60)}h {tBefore % 60}m</span>
                        <span className="shift-dash-table__hours-after">{Math.floor(tAfter / 60)}h {tAfter % 60}m</span>
                        <span className="shift-dash-table__hours-val">{Math.floor(tTotal / 60)}h {tTotal % 60}m</span>
                        <span></span>
                        <span className="shift-dash-table__cost shift-dash-table__cost--total">{tCost > 0 ? `$${tCost.toFixed(0)}` : '—'}</span>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Shift Log — Day-wise */}
            <div className="shift-dash-section">
              <div className="shift-dash-section__header">
                <h3 className="shift-dash-section__title">{showDeleted ? '🗑 Deleted Records' : 'Shift Log'}</h3>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  {canEdit && !showDeleted && (
                    <button className="shift-dash-add-btn" onClick={openAddShiftModal}>+ Add Shift</button>
                  )}
                  {canDeleteRecords && (deletedShifts.length > 0 || showDeleted) && (
                    <button className="sdw-deleted-toggle" onClick={() => { setShowDeleted(v => !v); setOnlyIssues(false); }}>
                      {showDeleted ? '← Back to Shift Log' : `🗑 Deleted (${deletedShifts.length})`}
                    </button>
                  )}
                  <span className="shift-dash-section__count">{filtered.length} records</span>
                </div>
              </div>

              {/* Possible duplicates / mis-taps in this period */}
              {!showDeleted && timesheetIssues.size > 0 && (() => {
                const zeroes = liveShifts.filter(x => timesheetIssues.get(x.id)?.kind === 'zero');
                return (
                  <div className="sdw-issues" role="status">
                    <span className="sdw-issues__icon">⚠️</span>
                    <div className="sdw-issues__body">
                      <strong>{timesheetIssues.size} record{timesheetIssues.size > 1 ? 's look' : ' looks'} like a duplicate or mis-tap</strong>
                      <span>0-minute punches, or times that overlap another record of the same person. Check them so hours aren't counted twice.</span>
                    </div>
                    <div className="sdw-issues__actions">
                      <button className="sdw-issues__btn" onClick={() => setOnlyIssues(v => !v)}>
                        {onlyIssues ? 'Show all' : 'Show only these'}
                      </button>
                      {canDeleteRecords && zeroes.length > 0 && (
                        <button className="sdw-issues__btn sdw-issues__btn--danger" onClick={() => deleteRecords(zeroes, '0-minute punch (mis-tap)')}>
                          🗑 Delete the {zeroes.length} 0-minute one{zeroes.length > 1 ? 's' : ''}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {loadingData ? (
                <div className="shift-loading">Loading shifts…</div>
              ) : filtered.length === 0 ? (
                <div className="shift-loading">{showDeleted ? 'No deleted records in this period.' : 'No shifts found for this period.'}</div>
              ) : (
                <div className="sdw-day-list">
                  {shiftsByDay.map(({ key, date, shifts: dayShifts }) => {
                    const dayTotalMin = dayShifts
                      .filter(s => s.status === 'completed')
                      .reduce((sum, s) => sum + (s.totalMinutes || 0), 0);
                    const activeInDay = dayShifts.filter(s => s.status === 'active' || s.status === 'on-break').length;
                    const todayKey = new Date().toLocaleDateString('en-CA');
                    const isToday = key === todayKey;
                    return (
                      <div key={key} className="sdw-day-group">
                        <div className={`sdw-day-header${isToday ? ' sdw-day-header--today' : ''}`}>
                          <div className="sdw-day-header__left">
                            <span className="sdw-day-header__weekday">
                              {date.toLocaleDateString('en-US', { weekday: 'long' })}
                            </span>
                            <span className="sdw-day-header__date">
                              {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                            {isToday && <span className="sdw-today-pill">Today</span>}
                          </div>
                          <div className="sdw-day-header__meta">
                            {activeInDay > 0 && (
                              <span className="sdw-meta-pill sdw-meta-pill--active">🟢 {activeInDay} active</span>
                            )}
                            <span className="sdw-meta-pill">{dayShifts.length} shift{dayShifts.length !== 1 ? 's' : ''}</span>
                            {dayTotalMin > 0 && (
                              <span className="sdw-meta-pill sdw-meta-pill--hours">
                                ⏰ {Math.floor(dayTotalMin / 60)}h {dayTotalMin % 60}m
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="sdw-day-shifts">
                          {dayShifts.map(s => {
                            const inTime = toDate(s.clockIn);
                            const outTime = toDate(s.clockOut);
                            const hrs = Math.floor((s.totalMinutes || 0) / 60);
                            const mins = (s.totalMinutes || 0) % 60;
                            const issue = timesheetIssues.get(s.id);
                            const deleted = isDeleted(s);
                            const deletedAt = toDate(s.deletedAt);
                            return (
                              <div key={s.id} className={`sdw-shift-card sdw-shift-card--${s.status === 'on-break' ? 'break' : s.status}${issue ? ' sdw-shift-card--issue' : ''}`}>
                                <div className="sdw-shift-card__emp">
                                  <div className="sdw-shift-card__avatar">{(s.userName || '?')[0].toUpperCase()}</div>
                                  <div className="sdw-shift-card__name">{s.userName}</div>
                                </div>
                                <div className="sdw-shift-card__times">
                                  <span className="sdw-shift-card__in">{fmtTime(inTime)}</span>
                                  <span className="sdw-shift-card__sep">→</span>
                                  <span className="sdw-shift-card__out">{outTime ? fmtTime(outTime) : '—'}</span>
                                </div>
                                {!deleted && (anomalies.byRecord.get(s.id) || []).filter((a, i, all) => all.findIndex(b => b.kind === a.kind) === i).slice(0, 3).map(a => (
                                  <span key={a.kind} className={`sdw-issue-badge sdw-issue-badge--${a.severity}`} title={a.detail}>⚠ {a.label}</span>
                                ))}
                                {deleted && (
                                  <span className="sdw-deleted-note">
                                    Deleted{s.deletedByName ? ` by ${s.deletedByName}` : ''}{deletedAt ? ` · ${deletedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}{s.deleteReason ? ` · ${s.deleteReason}` : ''}
                                  </span>
                                )}
                                <div className="sdw-shift-card__right">
                                  {s.status === 'completed' && (
                                    <span className="sdw-shift-card__hours">{hrs}h {mins}m</span>
                                  )}
                                  {!deleted && (
                                    <span className={`shift-dash-badge shift-dash-badge--${s.status === 'on-break' ? 'break' : s.status}`}>
                                      {s.status === 'active' ? '🟢 Active' : s.status === 'on-break' ? '☕ Break' : '✅ Done'}
                                    </span>
                                  )}
                                  {deleted ? (
                                    canDeleteRecords && <button className="sdw-restore-btn" onClick={() => restoreDeleted(s)}>↩ Restore</button>
                                  ) : canEdit && (
                                    myPendingEditsMap[s.id] ? (
                                      <span className="shift-dash-pending-badge" title="Change or deletion sent, awaiting admin approval">⏳</span>
                                    ) : (
                                      <>
                                        <button className="shift-dash-edit-btn" onClick={() => openEditModal(s)} title="Edit shift">✏️</button>
                                        {issue && (
                                          <button
                                            className="shift-dash-edit-btn shift-dash-del-btn"
                                            onClick={() => deleteRecords([s], issue.kind === 'zero' ? '0-minute punch (mis-tap)' : 'Duplicate record')}
                                            title="Delete this record (you can undo)"
                                            aria-label={`Delete ${s.userName}'s ${fmtTime(inTime)} record`}
                                          >🗑</button>
                                        )}
                                      </>
                                    )
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Request History (Admin / Owner) ── */}
            {canApprove && (
              <div className="shift-dash-section shift-dash-section--req-history">
                <div
                  className="shift-dash-section__header shift-dash-section__header--clickable"
                  onClick={() => {
                    const next = !showReqHistory;
                    setShowReqHistory(next);
                    if (next && reqHistory.length === 0) loadRequestHistory();
                  }}
                >
                  <h3 className="shift-dash-section__title">📋 Request History</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {reqHistory.length > 0 && (
                      <span className="shift-dash-section__count">{reqHistory.length} records</span>
                    )}
                    <span className="shift-dash-section__toggle">{showReqHistory ? '▲ Hide' : '▼ Show'}</span>
                  </div>
                </div>

                {showReqHistory && (
                  loadingHistory ? (
                    <div className="shift-loading" style={{ padding: '20px 0' }}>Loading history…</div>
                  ) : reqHistory.length === 0 ? (
                    <div className="shift-req-history__empty">No reviewed requests yet.</div>
                  ) : (
                    <div className="shift-req-history-wrap">
                    <div className="shift-req-history">
                      {/* Header row */}
                      <div className="shift-req-history__head">
                        <span>Employee</span>
                        <span>Type</span>
                        <span>Submitted By</span>
                        <span>Requested Times</span>
                        <span>Reviewed</span>
                        <span>Status</span>
                        <span>Reviewed By</span>
                      </div>
                      {reqHistory.map((req) => {
                        const isDelete = req.type === 'delete-shift';
                        // a deletion has no requested times — show the record it was about
                        const newIn   = toDate(isDelete ? req.originalClockIn : req.requestedClockIn);
                        const newOut  = toDate(isDelete ? req.originalClockOut : req.requestedClockOut);
                        const newTotal = newIn && newOut ? Math.round((newOut - newIn) / 60000) : 0;
                        const reviewedAt = req.reviewedAt?.toDate ? req.reviewedAt.toDate() : null;
                        const createdAt  = req.createdAt?.toDate  ? req.createdAt.toDate()  : null;
                        const isNewShift = req.type === 'new-shift';
                        const submittedByRole = req.submittedByRole || (req.submittedBy ? 'manager' : 'employee');
                        return (
                          <div key={req.id} className={`shift-req-history__row shift-req-history__row--${req.status}`}>
                            {/* Employee */}
                            <div className="shift-req-history__emp">
                              <span className="shift-req-history__emp-name">{req.userName}</span>
                              <span className="shift-req-history__emp-email">{req.userEmail}</span>
                            </div>
                            {/* Type */}
                            <span className={`shift-req-history__type shift-req-history__type--${isNewShift ? 'new' : isDelete ? 'delete' : 'edit'}`}>
                              {isNewShift ? '➕ New Shift' : isDelete ? '🗑 Delete' : '✏️ Hour Edit'}
                            </span>
                            {/* Submitted by */}
                            <div className="shift-req-history__by">
                              <span>{req.submittedByName || req.userName}</span>
                              <span className="shift-req-history__role">{submittedByRole}</span>
                              {createdAt && <span className="shift-req-history__date">{createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                            </div>
                            {/* Times */}
                            <div className="shift-req-history__times">
                              <span>{newIn ? newIn.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</span>
                              <span>{newIn ? fmtTime(newIn) : '—'} – {newOut ? fmtTime(newOut) : '—'}</span>
                              <span className="shift-req-history__duration">{Math.floor(newTotal / 60)}h {newTotal % 60}m</span>
                            </div>
                            {/* Reviewed at */}
                            <span className="shift-req-history__date">
                              {reviewedAt ? reviewedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                            </span>
                            {/* Status pill */}
                            <span className={`shift-req-history__status shift-req-history__status--${req.status}`}>
                              {req.status === 'approved' ? '✅ Approved' : req.status === 'rejected' ? '❌ Rejected' : req.status === 'superseded' ? '⧉ Duplicate' : '↩ Withdrawn'}
                            </span>
                            {/* Reviewed by */}
                            <span className="shift-req-history__reviewer">{req.reviewedByName || '—'}</span>
                          </div>
                        );
                      })}
                    </div>
                    </div>
                  )
                )}
              </div>
            )}

            <div className="shift-dash-refresh">
              <button className="shift-dash-refresh__btn" onClick={() => { loadShifts(); loadPendingRequests(); if (showReqHistory) loadRequestHistory(); }}>↻ Refresh Data</button>
            </div>
          </>
        )}

        {/* ── Planner Modal ── */}
        {planModal && (
          <div className="shift-edit-overlay" onClick={()=>!pmSaving&&setPlanModal(null)}>
            <form
              className="sp-modal"
              onClick={e=>e.stopPropagation()}
              onSubmit={e=>{e.preventDefault();savePlanModal();}}
              role="dialog" aria-modal="true" aria-label={planModal.mode==='add'?'Add shift':'Edit shift'}
            >
              <div className="sp-modal__header">
                <div>
                  <h2 className="sp-modal__title">{planModal.mode==='add'?'➕ Add Shift':'✏️ Edit Shift'}</h2>
                  <p className="sp-modal__sub">{planModal.userName} · {pDayName(planModal.date)} {pFmtDate(planModal.date)}</p>
                </div>
                <button type="button" className="sp-modal__close" onClick={()=>!pmSaving&&setPlanModal(null)} aria-label="Close">×</button>
              </div>

              {/* What's already booked for this person that day — tap one to edit it */}
              {pmSameDay.length > 0 && (
                <div className="sp-sameday">
                  <span className="sp-sameday__label">Already on {pDayName(planModal.date)}:</span>
                  {pmSameDay.map(s => (
                    <button
                      type="button" key={s.id}
                      className={`sp-sameday__chip${pmClash?.shift.id===s.id?' sp-sameday__chip--clash':''}`}
                      onClick={()=>openPlanEdit(null,s)}
                      title="Edit this shift"
                    >{pFmt12(s.startTime)}–{pFmt12(s.endTime)} ✏️</button>
                  ))}
                </div>
              )}

              {/* ── Availability warnings ── */}
              {pmAvailStatus === 'unavailable' && (
                <div className="sp-avail-warning sp-avail-warning--block">
                  <div className="sp-avail-warning__icon">🚫</div>
                  <div className="sp-avail-warning__body">
                    <strong>{planModal.userName}</strong> is marked <em>Not Available</em> on {pDayName(planModal.date)}s.
                    <label className="sp-avail-override-label">
                      <input type="checkbox" checked={pmAvailOverride} onChange={e=>setPmAvailOverride(e.target.checked)} />
                      Schedule anyway (override)
                    </label>
                  </div>
                </div>
              )}
              {pmAvailStatus === 'partial' && (
                <div className="sp-avail-warning sp-avail-warning--partial">
                  <span className="sp-avail-warning__icon">⚡</span>
                  <span><strong>{planModal.userName}</strong> has limited availability on {pDayName(planModal.date)}s — verify times match their available window.</span>
                </div>
              )}

              <div className="sp-modal__section-label">Quick Templates</div>
              <div className="sp-modal__templates">
                {SHIFT_TEMPLATES.map(t=>{
                  // templates that would double-book the person are greyed out
                  const taken = findShiftClash(planned, {
                    userId:planModal.userId, date:planModal.date, startTime:t.startTime, endTime:t.endTime, ignoreId:planModal.doc?.id,
                  });
                  const active = t.startTime===pmStart && t.endTime===pmEnd;
                  return (
                    <button
                      type="button" key={t.label} disabled={!!taken}
                      className={`sp-tpl-btn${active?' sp-tpl-btn--active':''}${taken?' sp-tpl-btn--taken':''}`}
                      onClick={()=>applyPlanTemplate(t.label)}
                      title={taken ? `Overlaps the ${pFmt12(taken.shift.startTime)}–${pFmt12(taken.shift.endTime)} shift already booked` : undefined}
                    >
                      <span className="sp-tpl-btn__name">{t.label}</span>
                      <span className="sp-tpl-btn__time">{pFmt12(t.startTime)}–{pFmt12(t.endTime)}</span>
                      {taken && <span className="sp-tpl-btn__tag">{taken.kind==='duplicate'?'already added':'overlaps'}</span>}
                    </button>
                  );
                })}
              </div>
              <div className="sp-modal__row">
                <div className="shift-edit-modal__field">
                  <label className="shift-edit-modal__label" htmlFor="pm-start">Start Time</label>
                  <input id="pm-start" type="time" className="shift-edit-modal__input" value={pmStart} onChange={e=>setPmStart(e.target.value)} />
                </div>
                <div className="shift-edit-modal__field">
                  <label className="shift-edit-modal__label" htmlFor="pm-end">End Time</label>
                  <input id="pm-end" type="time" className="shift-edit-modal__input" value={pmEnd} onChange={e=>setPmEnd(e.target.value)} />
                </div>
              </div>

              {/* Live double-booking check — the same person can't have two shifts at once */}
              {pmClash && (
                <div className="sp-avail-warning sp-avail-warning--block" role="alert">
                  <span className="sp-avail-warning__icon">⛔</span>
                  <div className="sp-avail-warning__body">
                    <span>
                      {pmClash.kind==='duplicate'
                        ? <><strong>{planModal.userName}</strong> already has this exact shift ({pFmt12(pmClash.shift.startTime)}–{pFmt12(pmClash.shift.endTime)}) on {pDayName(planModal.date)}.</>
                        : <>Overlaps <strong>{planModal.userName}</strong>'s {pFmt12(pmClash.shift.startTime)}–{pFmt12(pmClash.shift.endTime)} shift on {pDayName(planModal.date)}. Pick times that don't overlap.</>}
                    </span>
                    <button type="button" className="sp-clash__btn" onClick={()=>openPlanEdit(null,pmClash.shift)}>✏️ Edit that shift instead</button>
                  </div>
                </div>
              )}

              {pmHrs>0 ? (
                <div className="shift-edit-modal__preview">⏱ {pmHrs.toFixed(1)}h — {pFmt12(pmStart)} to {pFmt12(pmEnd)}</div>
              ) : pmStart && pmEnd ? (
                <div className="sp-time-error">End time must be after start time</div>
              ) : null}
              {pmHrs>0 && !pmClash && (
                <div className={`sp-week-impact${pmWeekAfter>=OVERTIME_THRESHOLD?' sp-week-impact--over':pmWeekAfter>=OVERTIME_THRESHOLD-4?' sp-week-impact--near':''}`}>
                  {(planModal.userName||'').split(' ')[0]}'s week: {pmWeekBefore.toFixed(1)}h → <strong>{pmWeekAfter.toFixed(1)}h</strong>
                  {pmWeekAfter>=OVERTIME_THRESHOLD ? ' · overtime' : pmWeekAfter>=OVERTIME_THRESHOLD-4 ? ' · near overtime' : ''}
                </div>
              )}

              {/* Same shift on other days of this week */}
              <div className="sp-modal__section-label">
                {planModal.mode==='add'?'Also add on':'Copy to other days'} <span className="sp-modal__section-hint">· same times</span>
              </div>
              <div className="sp-repeat">
                {pmRepeatDays.map(d => {
                  const on = !d.blocked && pmRepeat.includes(d.date);
                  return (
                    <button
                      type="button" key={d.date} disabled={d.blocked} aria-pressed={on}
                      className={`sp-repeat__day${on?' sp-repeat__day--on':''}`}
                      title={d.why || `${PLAN_DAYS_FULL[d.i]} ${pFmtDate(d.date)}`}
                      onClick={()=>setPmRepeat(prev => prev.includes(d.date) ? prev.filter(x=>x!==d.date) : [...prev, d.date])}
                    >
                      <span className="sp-repeat__name">{PLAN_DAYS[d.i]}</span>
                      <span className="sp-repeat__tag">{d.tag}</span>
                    </button>
                  );
                })}
                {pmRepeatDays.filter(d=>!d.blocked).length > 1 && (() => {
                  const open = pmRepeatDays.filter(d=>!d.blocked).map(d=>d.date);
                  const allOn = open.every(d=>pmRepeat.includes(d));
                  return (
                    <button type="button" className="sp-repeat__all" onClick={()=>setPmRepeat(allOn?[]:open)}>
                      {allOn?'Clear':'All open days'}
                    </button>
                  );
                })()}
              </div>

              <div className="shift-edit-modal__field">
                <label className="shift-edit-modal__label" htmlFor="pm-notes">Notes (optional)</label>
                <textarea id="pm-notes" className="shift-edit-modal__input shift-edit-modal__textarea"
                  placeholder="e.g. Cover for John…" value={pmNotes} onChange={e=>setPmNotes(e.target.value)} rows={2} />
              </div>

              {pmConfirmDel && planModal.doc ? (
                <div className="sp-del-confirm" role="alert">
                  <span className="sp-del-confirm__q">
                    Delete {planModal.userName}'s {pFmt12(planModal.doc.startTime)}–{pFmt12(planModal.doc.endTime)} shift on {pDayName(planModal.date)}?
                  </span>
                  <div className="shift-edit-modal__actions">
                    <button type="button" className="shift-edit-modal__btn shift-edit-modal__btn--cancel" onClick={()=>setPmConfirmDel(false)} disabled={pmSaving}>Keep it</button>
                    <button type="button" className="shift-edit-modal__btn shift-edit-modal__btn--danger" onClick={deleteFromModal} disabled={pmSaving} autoFocus>
                      {pmSaving?'Deleting…':'🗑 Yes, delete'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="shift-edit-modal__actions">
                  {planModal.mode==='edit' && (
                    <button type="button" className="shift-edit-modal__btn shift-edit-modal__btn--delete" onClick={()=>setPmConfirmDel(true)} disabled={pmSaving} title="Delete this shift" aria-label="Delete shift">
                      🗑<span className="shift-edit-modal__btn-label"> Delete</span>
                    </button>
                  )}
                  <button type="button" className="shift-edit-modal__btn shift-edit-modal__btn--cancel" onClick={()=>!pmSaving&&setPlanModal(null)} disabled={pmSaving}>Cancel</button>
                  <button type="submit" className="shift-edit-modal__btn shift-edit-modal__btn--save" disabled={pmSaving||pmHrs<=0||!!pmClash||(pmAvailStatus==='unavailable'&&!pmAvailOverride)}>
                    {pmSaving ? 'Saving…'
                      : planModal.mode==='add' ? (pmRepeatDates.length ? `Add ${pmRepeatDates.length+1} Shifts` : 'Add Shift')
                      : (pmRepeatDates.length ? `Save + Add ${pmRepeatDates.length}` : 'Save Changes')}
                  </button>
                </div>
              )}
            </form>
          </div>
        )}

        {/* ── 👥 Team: order, deactivate, reactivate ── */}
        {teamOpen && (
          <TeamManager
            staff={teamStaff}
            inactive={team.inactive}
            canDeactivate={canApprove}
            saveState={teamSave}
            onReorder={reorderTeam}
            loadUpcoming={loadUpcomingFor}
            onDeactivate={deactivateMember}
            onReactivate={emp => reactivateMember(emp)}
            onClose={closeTeam}
          />
        )}

        {/* ── Clear week confirm ── */}
        {clearModal && (() => {
          const targets = clearTargets(clearModal);
          const people = new Set(targets.map(p => p.userId)).size;
          const pastDays = weekDays.filter(d => d < todayISO);
          const viewName = planFilterRole==='manager' ? 'Managers' : 'Employees';
          // only ask "whose shifts" when the answer changes what gets deleted
          const askScope = planFilterRole!=='all'
            && clearTargets({ ...clearModal, scope: 'view' }).length !== clearTargets({ ...clearModal, scope: 'all' }).length;
          return (
            <div className="shift-edit-overlay" onClick={()=>!clearBusy&&setClearModal(null)}>
              <div className="sp-modal sp-clear-modal" role="dialog" aria-modal="true" aria-label="Clear week" onClick={e=>e.stopPropagation()}>
                <div className="sp-modal__header">
                  <div>
                    <h2 className="sp-modal__title">🗑 Clear {weekRangeLabel(weekStart)}?</h2>
                    <p className="sp-modal__sub">{weekRelLabel(weekStart, thisWeekStart)} · deletes the planned shifts below</p>
                  </div>
                  <button type="button" className="sp-modal__close" onClick={()=>!clearBusy&&setClearModal(null)} aria-label="Close">×</button>
                </div>

                {askScope && (
                  <>
                    <div className="sp-modal__section-label">Whose shifts</div>
                    <div className="sp-clear-opts" role="radiogroup" aria-label="Whose shifts">
                      {[
                        ['view', `${viewName} only`, `what you're viewing`],
                        ['all',  'Everyone',          'all staff this week'],
                      ].map(([scope, label, hint]) => (
                        <label key={scope} className={`sp-clear-opt${clearModal.scope===scope?' sp-clear-opt--on':''}`}>
                          <input type="radio" name="clear-scope" checked={clearModal.scope===scope} onChange={()=>setClearModal(m=>({...m,scope}))} />
                          <span className="sp-clear-opt__label">{label}</span>
                          <span className="sp-clear-opt__hint">{hint} · {clearTargets({ ...clearModal, scope }).length} shifts</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}

                {pastDays.length > 0 && pastDays.length < 7 && (
                  <label className="sp-clear-check">
                    <input type="checkbox" checked={clearModal.keepPast} onChange={e=>setClearModal(m=>({...m,keepPast:e.target.checked}))} />
                    Keep days that already happened ({PLAN_DAYS[0]}{pastDays.length>1?` – ${PLAN_DAYS[pastDays.length-1]}`:''})
                  </label>
                )}

                <div className={`sp-clear-summary${targets.length?'':' sp-clear-summary--none'}`}>
                  {targets.length
                    ? <>Deletes <strong>{targets.length} shift{targets.length>1?'s':''}</strong> for {people} {people===1?'person':'people'}. You can undo right after.</>
                    : 'Nothing to clear with these options.'}
                </div>

                <div className="shift-edit-modal__actions">
                  <button type="button" className="shift-edit-modal__btn shift-edit-modal__btn--cancel" onClick={()=>setClearModal(null)} disabled={clearBusy}>Cancel</button>
                  <button type="button" className="shift-edit-modal__btn shift-edit-modal__btn--danger" onClick={clearWeek} disabled={clearBusy||!targets.length}>
                    {clearBusy ? 'Clearing…' : `Delete ${targets.length} shift${targets.length===1?'':'s'}`}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Edit / Add Shift Modal ── */}
        {editModal && (
          <div className="shift-edit-overlay" onClick={() => !editSaving && setEditModal(null)}>
            <div className="shift-edit-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="shift-edit-modal__title">
                {editModal === 'new' ? '➕ Add Manual Shift' : '✏️ Correct Shift'}
              </h3>
              {editModal === 'new' && (
                <div className="shift-edit-modal__field">
                  <label className="shift-edit-modal__label">Employee</label>
                  <select
                    className="shift-edit-modal__input"
                    value={editEmployee}
                    onChange={(e) => setEditEmployee(e.target.value)}
                  >
                    {activeStaff.map((s) => (
                      <option key={s.uid} value={s.uid}>{s.name} ({s.email})</option>
                    ))}
                    {/* still listed so a final missed shift can be added for payroll */}
                    {teamStaff.some(s => isInactive(s.uid)) && (
                      <optgroup label="Inactive">
                        {teamStaff.filter(s => isInactive(s.uid)).map((s) => (
                          <option key={s.uid} value={s.uid}>{s.name} ({s.email})</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}
              {editModal !== 'new' && (
                <div className="shift-edit-modal__info">
                  <strong>{editModal.userName}</strong>
                  <span>{editModal.userEmail}</span>
                </div>
              )}
              <div className="shift-edit-modal__field">
                <label className="shift-edit-modal__label" htmlFor="edit-clock-in">Clock In</label>
                <input
                  id="edit-clock-in"
                  type="datetime-local"
                  className="shift-edit-modal__input"
                  value={editClockIn}
                  onChange={(e) => setEditClockIn(e.target.value)}
                />
              </div>
              <div className="shift-edit-modal__field">
                <label className="shift-edit-modal__label" htmlFor="edit-clock-out">Clock Out</label>
                <input
                  id="edit-clock-out"
                  type="datetime-local"
                  className="shift-edit-modal__input"
                  value={editClockOut}
                  onChange={(e) => setEditClockOut(e.target.value)}
                />
              </div>
              {editClockIn && editClockOut && (
                <div className="shift-edit-modal__preview">
                  Total: {Math.floor(Math.max(0, (new Date(editClockOut) - new Date(editClockIn)) / 60000) / 60)}h{' '}
                  {Math.round(Math.max(0, (new Date(editClockOut) - new Date(editClockIn)) / 60000) % 60)}m
                </div>
              )}
              {!canDirectEdit && (
                <div className="shift-edit-modal__field">
                  <label className="shift-edit-modal__label">
                    {editModal === 'new' ? 'Notes / reason (optional)' : 'Reason for change'}
                    {editModal !== 'new' && <span style={{color:'#ef4444'}}> *</span>}
                  </label>
                  <textarea
                    className="shift-edit-modal__input shift-edit-modal__textarea"
                    placeholder={editModal === 'new' ? 'e.g. Employee worked an extra shift not captured in system…' : 'e.g. Employee forgot to clock out, worked extra time…'}
                    rows={3}
                    value={managerEditReason}
                    onChange={(e) => setManagerEditReason(e.target.value)}
                  />
                </div>
              )}
              {/* New or corrected times that land on their other records: say so (owner/admin can replace them) */}
              {addOverlaps.length > 0 && (
                <div className="sp-avail-warning sp-avail-warning--block" role="alert">
                  <span className="sp-avail-warning__icon">⚠️</span>
                  <div className="sp-avail-warning__body">
                    <span>
                      <strong>{(editModal === 'new' ? (allStaff.find(x => x.uid === editEmployee) || {}).name : editModal.userName) || 'They'}</strong> already {addOverlaps.length > 1 ? `has ${addOverlaps.length} records` : 'has a record'} in this time:{' '}
                      {addOverlaps.map(o => `${fmtTime(toDate(o.clockIn))}–${o.clockOut ? fmtTime(toDate(o.clockOut)) : 'now'}`).join(', ')}
                    </span>
                    {canDirectEdit ? (
                      <label className="sp-avail-override-label">
                        <input type="checkbox" checked={replaceOverlaps} onChange={e => setReplaceOverlaps(e.target.checked)} />
                        Replace {addOverlaps.length > 1 ? 'them' : 'it'} with this shift (recommended — avoids double hours)
                      </label>
                    ) : (
                      <span>The admin sees this when approving and can replace {addOverlaps.length > 1 ? 'them' : 'it'}.</span>
                    )}
                  </div>
                </div>
              )}

              {editDelete && editModal !== 'new' ? (
                <div className="sp-del-confirm" role="alert">
                  <span className="sp-del-confirm__q">
                    Delete {editModal.userName}'s {fmtTime(toDate(editModal.clockIn))} – {editModal.clockOut ? fmtTime(toDate(editModal.clockOut)) : 'open'} record?
                    {' '}It stops counting toward hours — you can undo, or restore it from Deleted.
                    {deleteNeedsReason && ' The reason is shown to the owner.'}
                  </span>
                  <input
                    className="shift-edit-modal__input sdw-del-reason"
                    placeholder={deleteNeedsReason ? 'Reason (required) — e.g. duplicate punch' : 'Reason (optional) — e.g. duplicate punch'}
                    value={editDelete.reason}
                    onChange={e => setEditDelete(d => ({ ...d, reason: e.target.value, error: '' }))}
                    aria-label="Reason for deleting"
                  />
                  {editDelete.error && <div className="shift-req-error">{editDelete.error}</div>}
                  <div className="shift-edit-modal__actions">
                    <button className="shift-edit-modal__btn shift-edit-modal__btn--cancel" onClick={() => setEditDelete(null)} disabled={editSaving}>Keep it</button>
                    <button className="shift-edit-modal__btn shift-edit-modal__btn--danger" onClick={confirmEditDelete} disabled={editSaving}>
                      {editSaving ? '…' : '🗑 Delete record'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="shift-edit-modal__actions">
                  {editModal !== 'new' && canDeleteRecords && (
                    <button
                      className="shift-edit-modal__btn shift-edit-modal__btn--delete"
                      onClick={() => setEditDelete({ reason: '' })}
                      disabled={editSaving}
                      title="Delete this record"
                      aria-label="Delete record"
                    >🗑<span className="shift-edit-modal__btn-label"> Delete</span></button>
                  )}
                  <button
                    className="shift-edit-modal__btn shift-edit-modal__btn--cancel"
                    onClick={() => { setEditModal(null); setManagerEditReason(''); setEditDelete(null); }}
                    disabled={editSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="shift-edit-modal__btn shift-edit-modal__btn--save"
                    onClick={handleSaveEdit}
                    disabled={editSaving}
                  >
                    {editSaving ? 'Saving…'
                      : !canDirectEdit ? (addOverlaps.length ? '📨 Submit anyway' : '📨 Submit for Approval')
                      : addOverlaps.length
                        ? (replaceOverlaps ? `${editModal === 'new' ? 'Add' : 'Save'} & replace ${addOverlaps.length}` : `${editModal === 'new' ? 'Add' : 'Save'} anyway`)
                        : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* ── Availability Modal ── */}
        {availModal && (
          <div className="shift-edit-overlay" onClick={()=>!availSaving&&setAvailModal(null)}>
            <div className="sp-avail-modal" onClick={e=>e.stopPropagation()}>
              <div className="sp-modal__header">
                <div>
                  <h2 className="sp-modal__title">📅 Set Availability</h2>
                  <p className="sp-modal__sub">{availModal.userName} — weekly recurring schedule</p>
                </div>
                <button className="sp-modal__close" onClick={()=>!availSaving&&setAvailModal(null)}>×</button>
              </div>
              <p className="sp-avail-modal__hint">Set when this employee is available each week. Managers will see conflicts when scheduling.</p>
              <div className="sp-avail-days">
                {PLAN_DAYS_FULL.map(day => {
                  const d = availDays[day] || { type: 'available' };
                  return (
                    <div key={day} className={`sp-avail-day sp-avail-day--${d.type}`}>
                      <div className="sp-avail-day__header">
                        <span className="sp-avail-day__name">{day}</span>
                        <div className="sp-avail-day__type-btns">
                          {[
                            { t: 'available',   icon: '✅', label: 'Available' },
                            { t: 'partial',     icon: '⚡', label: 'Partial' },
                            { t: 'unavailable', icon: '🚫', label: 'Not Available' },
                          ].map(({ t, icon, label }) => (
                            <button
                              key={t}
                              className={`sp-avail-type-btn sp-avail-type-btn--${t}${d.type===t?' sp-avail-type-btn--active':''}`}
                              onClick={() => setAvailDays(prev => ({
                                ...prev,
                                [day]: { ...prev[day], type: t },
                              }))}
                            >
                              {icon} <span>{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      {d.type === 'partial' && (
                        <div className="sp-avail-day__times">
                          <label>Available from</label>
                          <input
                            type="time"
                            className="shift-edit-modal__input sp-avail-time-input"
                            value={d.startTime || '09:00'}
                            onChange={e => setAvailDays(prev => ({ ...prev, [day]: { ...prev[day], startTime: e.target.value } }))}
                          />
                          <label>to</label>
                          <input
                            type="time"
                            className="shift-edit-modal__input sp-avail-time-input"
                            value={d.endTime || '17:00'}
                            onChange={e => setAvailDays(prev => ({ ...prev, [day]: { ...prev[day], endTime: e.target.value } }))}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="shift-edit-modal__actions">
                <button className="shift-edit-modal__btn shift-edit-modal__btn--cancel" onClick={()=>!availSaving&&setAvailModal(null)} disabled={availSaving}>Cancel</button>
                <button className="shift-edit-modal__btn shift-edit-modal__btn--save" onClick={saveAvailModal} disabled={availSaving}>
                  {availSaving ? 'Saving…' : '💾 Save Availability'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Labor Rate Modal ── */}
        {rateModal && (
          <div className="shift-edit-overlay" onClick={() => !rateSaving && setRateModal(null)}>
            <div className="shift-edit-modal" onClick={e => e.stopPropagation()}>
              <h2 className="shift-edit-modal__title">💰 Set Hourly Rate</h2>
              <div className="shift-edit-modal__info">
                <strong>{rateModal.userName}</strong>
                <span>Enter hourly pay rate for labor cost tracking</span>
              </div>
              <div className="shift-edit-modal__field">
                <label className="shift-edit-modal__label">Hourly Rate ($)</label>
                <input className="shift-edit-modal__input" type="number" min="0" step="0.50" placeholder="e.g. 12.00" value={rateInput} onChange={e => setRateInput(e.target.value)} />
              </div>
              {rateInput && !isNaN(parseFloat(rateInput)) && (
                <div className="shift-edit-modal__preview">${parseFloat(rateInput).toFixed(2)} / hour</div>
              )}
              <div className="shift-edit-modal__actions">
                <button className="shift-edit-modal__btn shift-edit-modal__btn--cancel" onClick={() => setRateModal(null)} disabled={rateSaving}>Cancel</button>
                <button className="shift-edit-modal__btn shift-edit-modal__btn--save" onClick={saveLaborRate} disabled={rateSaving}>{rateSaving ? 'Saving…' : 'Save Rate'}</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Shift Swap Modal ── */}
        {swapModal && (
          <div className="shift-edit-overlay" onClick={() => !swapSaving && setSwapModal(null)}>
            <div className="shift-edit-modal" onClick={e => e.stopPropagation()}>
              <h2 className="shift-edit-modal__title">🔄 Request Shift Swap</h2>
              <div className="shift-edit-modal__info">
                <strong>{swapModal.userName}'s shift</strong>
                <span>{pFmt12(swapModal.startTime)} – {pFmt12(swapModal.endTime)} on {swapModal.shiftDate} ({swapModal.position})</span>
              </div>
              <div className="shift-edit-modal__field">
                <label className="shift-edit-modal__label">Swap with</label>
                <select className="shift-edit-modal__input" value={swapTargetUser} onChange={e => setSwapTargetUser(e.target.value)}>
                  <option value="">Select employee…</option>
                  {activeStaff.filter(s => s.uid !== swapModal.userId).map(s => {
                    // can't hand the shift to someone already working then
                    const busy = findShiftClash(planned, { userId: s.uid, date: swapModal.shiftDate, startTime: swapModal.startTime, endTime: swapModal.endTime });
                    return (
                      <option key={s.uid} value={s.uid} disabled={!!busy}>
                        {s.name} ({s.role}){busy ? ` — busy ${pFmt12(busy.shift.startTime)}–${pFmt12(busy.shift.endTime)}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="shift-edit-modal__actions">
                <button className="shift-edit-modal__btn shift-edit-modal__btn--cancel" onClick={() => setSwapModal(null)} disabled={swapSaving}>Cancel</button>
                <button className="shift-edit-modal__btn shift-edit-modal__btn--save" onClick={submitSwapRequest} disabled={swapSaving || !swapTargetUser}>{swapSaving ? 'Submitting…' : 'Submit Swap'}</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ShiftDashboard;
